import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Section, Card, Badge, Alert, Button, Input, Label, Table } from "../design-system";
import { useAuth } from "../auth/AuthProvider";
import { Package, Truck, ClipboardList, AlertTriangle, Search, FileText, MapPin, Calendar, Layers, Download, XCircle, Inbox, Clock } from "lucide-react";
import { carrierPreferredStatus, sortCarriersByPreferred } from "../lib/carrierHints";
import { EmailImportPendingCard } from "./Dashboards";

const DATE_FIELDS = [
  { v: "requested", l: "Data consegna richiesta" },
  { v: "confirmed", l: "Data consegna confermata" },
  { v: "expected",  l: "Data prevista/carico" },
  { v: "received",  l: "Data ricezione ordine" },
];
function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoDaysFromToday(d) {
  const x = new Date();
  x.setDate(x.getDate() + d);
  return x.toISOString().slice(0, 10);
}
function weekBounds(offsetWeeks = 0) {
  const d = new Date();
  const day = (d.getDay() + 6) % 7; // Mon = 0
  const monday = new Date(d);
  monday.setDate(d.getDate() - day + offsetWeeks * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return [monday.toISOString().slice(0, 10), sunday.toISOString().slice(0, 10)];
}

const TABS = [
  { id: "ricevuti",       label: "Ricevuti",         kpiKey: "ricevuti_da_processare" },
  { id: "da_preparare",   label: "Da preparare",     kpiKey: "da_preparare" },
  { id: "in_piano",       label: "In Piano Carichi", kpiKey: null },
  { id: "in_consegna",    label: "In consegna",      kpiKey: "in_consegna" },
  { id: "consegnati",     label: "Consegnati",       kpiKey: null },
];

const SHIP_STATUS_IT = {
  DRAFT: "In preparazione",
  IN_TRANSIT: "In consegna",
  DELIVERED: "Consegnata",
  CLOSED: "Chiusa",
  VOIDED: "Annullata",
};

const ORDER_STATUS_IT = {
  RECEIVED: "Ricevuto",
  WAITING_PAYMENT: "Attesa pagamento",
  PROCESSING: "In preparazione",
  SHIPPING: "In spedizione",
  SHIPPING: "In consegna",
  IN_DELIVERY: "In consegna",  // deprecated: kept for legacy audit only
  DELIVERED: "Consegnato",
  READY_FOR_PICKUP: "Pronto ritiro",
};

function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString("it-IT") : ""; }
function fmtKg(n) { return Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 }); }

function KpiTile({ icon, label, value, tone = "info", testId }) {
  const bgTone = {
    info: "#eff6ff", warning: "#fffbeb", danger: "#fef2f2",
    success: "#f0fdf4", neutral: "#f8fafc",
  }[tone];
  const fgTone = {
    info: "#1d4ed8", warning: "#b45309", danger: "#b91c1c",
    success: "#15803d", neutral: "#334155",
  }[tone];
  return (
    <Card data-testid={testId} style={{ background: bgTone }}>
      <Card.Body style={{ padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: fgTone }}>{icon}</span>
          <span className="muted" style={{ fontSize: ".78rem", textTransform: "uppercase", letterSpacing: ".04em" }}>{label}</span>
        </div>
        <div style={{ fontSize: "1.75rem", fontWeight: 700, color: fgTone, marginTop: 4 }}>{value ?? 0}</div>
      </Card.Body>
    </Card>
  );
}

// Italian mapping for backend error codes surfaced in ShipmentActionsInline
const IT_ERR = {
  missing_carrier: "Vettore mancante: assegna un vettore prima di confermare la partenza.",
  missing_ddt: "DDT mancante: carica il PDF del DDT prima di confermare la partenza.",
  missing_expected_delivery_date: "Data presunta di consegna mancante: impostala prima di confermare la partenza.",
  shipment_not_found: "Spedizione non trovata.",
  shipment_voided: "La spedizione è stata annullata.",
  pickup_ddt_missing: "Carica il DDT prima di rendere l'ordine disponibile per il ritiro.",
  pickup_shipment_missing: "Prendi in carico l'ordine (PROCESSING) prima di segnalarlo pronto per il ritiro.",
  order_not_pickup: "Solo gli ordini con ritiro cliente possono essere segnati come pronti per il ritiro.",
  order_not_found: "Ordine non trovato.",
  pickup_location_locked: "La sede di ritiro non può più essere modificata perché l'ordine è già in preparazione.",
  pickup_location_invalid: "La sede selezionata non è valida o non è attiva.",
  pickup_rule_id_required: "Seleziona una sede di ritiro.",
  pickup_rule_id_invalid: "Sede di ritiro non valida.",
  use_mark_departed_endpoint: "Per mettere la spedizione in consegna usa la funzione Conferma partenza.",
};

function _extractErrKey(raw) {
  // Backend può restituire detail come stringa oppure dict {code, message}.
  if (!raw) return "";
  if (typeof raw === "object") return String(raw.code || raw.message || "").trim();
  return String(raw).replace(/^"|"$/g, "").trim();
}
function humanErr(raw) {
  if (!raw) return "";
  const key = _extractErrKey(raw);
  if (IT_ERR[key]) return IT_ERR[key];
  // Se raw è un dict con message già italiano, usa quello.
  if (typeof raw === "object" && raw.message) return raw.message;
  // invalid_status_<X> → messaggio parlante
  const m = key.match(/^invalid_status_([A-Z_]+)$/);
  if (m) return `Operazione non consentita: lo stato attuale dell'ordine è ${ORDER_STATUS_IT[m[1]] || m[1]}.`;
  return typeof raw === "string" ? raw : key || "Errore imprevisto";
}

function ReceivedActionsInline({ entry, onDone }) {
  const { apiPost } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [holdFile, setHoldFile] = useState(null);

  const doStartProcessing = async () => {
    if (!window.confirm(`Prendere in carico l'ordine ${entry.number}? Passa a PROCESSING.`)) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      // Fix #AUTH-401 (stesso pattern di doEtaUpdate/saveEta): usa apiPost
      // centralizzato invece di raw fetch + `Bearer ${token || ""}`, che
      // spediva "Bearer null"/"Bearer undefined" quando il token era falsy
      // e faceva restituire al backend il 401 grezzo `{"detail":"Invalid or
      // expired token"}` mostrato a UI.
      await apiPost(`/api/orders/${entry.id}/start-processing`, {});
      setOk("Ordine → PROCESSING");
      setTimeout(() => onDone(), 700);
    } catch (e) {
      setErr(humanErr(e?.data?.detail || e?.message || String(e)));
    } finally { setBusy(false); }
  };

  const doPaymentHold = async () => {
    const reason = (holdReason || "").trim();
    if (!reason) { setErr("Motivo obbligatorio (uso interno, non esposto al cliente)."); return; }
    setBusy(true); setErr(null); setOk(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const authToken = (typeof window !== "undefined" && window.localStorage?.getItem("access_token")) || "";
      const fd = new FormData();
      fd.append("reason", reason);
      if (holdFile) fd.append("file", holdFile);
      const res = await fetch(`${API}/api/orders/${entry.id}/payment-hold`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(humanErr(j?.detail || `Errore ${res.status}`));
      }
      setOk("Payment Hold applicato — ordine → WAITING_PAYMENT");
      setHoldOpen(false); setHoldReason(""); setHoldFile(null);
      setTimeout(() => onDone(), 700);
    } catch (e) {
      setErr(humanErr(e?.data?.detail || e?.message || String(e)));
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #e5e7eb" }}
         data-testid={`log-received-actions-${entry.number}`}>
      {err && <Alert tone="danger">Errore: {err}</Alert>}
      {ok && <Alert tone="success">{ok}</Alert>}
      {!holdOpen ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button size="sm" variant="primary" loading={busy} disabled={busy}
                  onClick={doStartProcessing}
                  data-testid={`btn-start-processing-${entry.number}`}>
            Prendi in carico → PROCESSING
          </Button>
          <Button size="sm" variant="secondary" loading={busy} disabled={busy}
                  onClick={() => setHoldOpen(true)}
                  data-testid={`btn-open-hold-${entry.number}`}>
            Payment Hold
          </Button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 260px" }}>
            <Label>Motivo interno (obbligatorio)</Label>
            <Input value={holdReason} onChange={(e) => setHoldReason(e.target.value)}
                   placeholder="Es: posizione insoluta 2026-08"
                   data-testid={`inp-hold-reason-${entry.number}`} />
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
              Non esposto al cliente. Auditato con il tuo utente.
            </div>
            <div style={{ marginTop: 8 }}>
              <Label>Allega PDF (opzionale)</Label>
              <input type="file" accept="application/pdf"
                     data-testid={`inp-hold-file-${entry.number}`}
                     onChange={(e) => setHoldFile(e.target.files?.[0] || null)}
                     style={{ display: "block" }} />
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                Es. estratto conto. Solo interno, non inviato al cliente.
              </div>
            </div>
          </div>
          <Button size="sm" variant="primary" loading={busy} disabled={busy || !holdReason.trim()}
                  onClick={doPaymentHold}
                  data-testid={`btn-hold-confirm-${entry.number}`}>
            Applica Hold
          </Button>
          <Button size="sm" variant="secondary" loading={busy} disabled={busy}
                  onClick={() => { setHoldOpen(false); setHoldReason(""); setErr(null); }}
                  data-testid={`btn-hold-cancel-${entry.number}`}>
            Annulla
          </Button>
        </div>
      )}
    </div>
  );
}

function PickupLocationChangeCard({ prep, onDone }) {
  const { apiGet, apiPatch } = useAuth();
  const [locs, setLocs] = useState([]);
  const [selected, setSelected] = useState(prep.order?.pickup_location_snapshot?.id || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);

  useEffect(() => {
    apiGet("/api/pickup-locations").then(setLocs).catch(() => setLocs([]));
  }, []);

  const current = prep.order?.pickup_location_snapshot || {};
  const changed = selected && selected !== current.id;

  const save = async () => {
    setErr(null); setOk(null); setBusy(true);
    try {
      await apiPatch(`/api/orders/${prep.order.id}/pickup-location`,
                     { pickup_rule_id: selected });
      setOk("Sede di ritiro aggiornata.");
      setTimeout(() => onDone?.(), 400);
    } catch (e) {
      const raw = e?.data?.detail || e?.message || String(e);
      setErr(humanErr(raw));
    } finally { setBusy(false); }
  };

  return (
    <Section title="Sede di ritiro"
             subtitle="Modificabile solo finché l'ordine è in RECEIVED">
      <Card data-testid="pickup-location-change-card"><Card.Body>
        {err && <Alert tone="danger" data-testid="pickup-loc-error">{err}</Alert>}
        {ok && <Alert tone="success" data-testid="pickup-loc-ok">{ok}</Alert>}

        <div style={{ marginBottom: 8, fontSize: ".85rem", color: "#666" }}>Sede attuale</div>
        <Card style={{ background: "#F3F6FA", marginBottom: 12 }}
              data-testid="pickup-loc-current">
          <Card.Body style={{ padding: 12 }}>
            <div style={{ fontWeight: 600 }}>{current.warehouse_name || "—"}</div>
            {current.address && <div>{current.address}</div>}
            {(current.zip_code || current.city) && (
              <div>{current.zip_code} {current.city}{current.province ? ` (${current.province})` : ""}</div>
            )}
            {current.phone && <div>Tel. {current.phone}</div>}
          </Card.Body>
        </Card>

        <Label>Nuova sede</Label>
        <select className="ds-input" value={selected || ""}
                data-testid="pickup-loc-select"
                onChange={(e) => setSelected(e.target.value || null)}>
          <option value="">— seleziona —</option>
          {locs.map((p) => (
            <option key={p.id} value={p.id}>
              {p.warehouse_name}{p.city ? ` · ${p.city}` : ""}{p.province ? ` (${p.province})` : ""}
            </option>
          ))}
        </select>

        <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="primary" onClick={save} loading={busy} disabled={busy || !changed}
                  data-testid="btn-pickup-loc-save">
            {busy ? "Salvataggio…" : "Salva nuova sede"}
          </Button>
        </div>
      </Card.Body></Card>
    </Section>
  );
}

function PickupReadyCard({ prep, token, onDone }) {
  const { apiPost, apiGet } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [ddtFile, setDdtFile] = useState(null);
  const [ddtNumber, setDdtNumber] = useState("");
  const [ddtDate, setDdtDate] = useState("");
  const [ddtKind, setDdtKind] = useState("STANDARD");
  const [ddtBusy, setDdtBusy] = useState(false);
  // v43.32 — Modalità 1 (Cerca DDT dal registro Zucchetti + folder fallback).
  // Mirrors the LoadPlanPages inline lookup pattern; reuses canonical existing
  // endpoints (GET /ddt/search + POST /ddt/attach-from-folder). No auto-attach.
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupErr, setLookupErr] = useState(null);
  const [lookup, setLookup] = useState(null);
  const [lookupDisabled, setLookupDisabled] = useState(false);

  const shp = (prep.existing_shipments || []).find(s => s.status !== "VOIDED")
            || (prep.existing_shipments || [])[0];
  const hasDdt = !!shp?.has_ddt;
  const loc = prep.order?.pickup_location_snapshot || null;

  const uploadDdt = async () => {
    setErr(null); setOk(null);
    if (!shp?.id) { setErr("Prendi in carico l'ordine prima di caricare il DDT."); return; }
    if (!ddtFile || !ddtNumber.trim() || !ddtDate) {
      setErr("Compila numero DDT, data e seleziona il PDF.");
      return;
    }
    // v43.32 — Guard token like doEtaUpdate (line 500-506): raw fetch here
    // needs a valid Bearer; without it FastAPI returns
    // {"detail":"Invalid or expired token"}. Fail early with a meaningful
    // message so the surrounding session-refresh flow can kick in.
    if (!token) {
      setErr("Sessione scaduta — ricarica la pagina per rientrare.");
      return;
    }
    setDdtBusy(true);
    try {
      const fd = new FormData();
      fd.append("ddt_number", ddtNumber.trim());
      fd.append("ddt_date", ddtDate);
      fd.append("kind", ddtKind);
      fd.append("file", ddtFile);
      const res = await fetch(`${API}/api/shipments/${shp.id}/ddt`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(humanErr(j?.detail?.code || j?.detail || `Errore ${res.status}`));
      }
      setOk("DDT caricato.");
      setDdtFile(null); setDdtNumber(""); setDdtDate("");
      onDone?.();
    } catch (e) {
      setErr(e.message || "Errore upload DDT");
    } finally { setDdtBusy(false); }
  };

  const runLookup = async () => {
    setLookupErr(null); setLookup(null);
    const q = ddtNumber.trim();
    if (!shp?.id) {
      setLookupErr("Prendi in carico l'ordine prima di cercare il DDT.");
      return;
    }
    if (!q) {
      setLookupErr("Inserisci il numero DDT (es. 2382/2026) prima di cercare.");
      return;
    }
    setLookupBusy(true);
    try {
      const res = await apiGet(
        `/api/shipments/${shp.id}/ddt/search?ddt_number=${encodeURIComponent(q)}`,
      );
      setLookup(res);
    } catch (e) {
      if (e?.status === 503) { setLookupDisabled(true); }
      else {
        const d = e?.data?.detail;
        const msg = Array.isArray(d)
          ? "Numero DDT non valido oppure ricerca non eseguibile."
          : (typeof d === "string" ? d : (e?.message || String(e)));
        setLookupErr(msg);
      }
    } finally { setLookupBusy(false); }
  };

  const attachFromRegistry = async (match) => {
    setLookupErr(null); setLookupBusy(true);
    try {
      const res = await apiPost(
        `/api/shipments/${shp.id}/ddt/attach-from-folder`,
        {
          filename: match.filename,
          ddt_number: ddtNumber.trim() || null,
          ddt_date: ddtDate || null,
          source: match.source || lookup?.source || undefined,
          kind: ddtKind,
        },
      );
      const already = res?.source === "already_attached";
      setOk(already ? "DDT già allegato a questa spedizione." : "DDT allegato dal registro.");
      setDdtFile(null); setDdtNumber(""); setDdtDate("");
      setLookup(null);
      onDone?.();
    } catch (e) {
      const d = e?.data?.detail;
      const msg = Array.isArray(d)
        ? "Impossibile allegare il DDT trovato."
        : (typeof d === "string" ? d : (e?.message || String(e)));
      setLookupErr(msg);
    } finally { setLookupBusy(false); }
  };

  const markReady = async () => {
    setErr(null); setOk(null); setBusy(true);
    try {
      await apiPost(`/api/orders/${prep.order.id}/mark-ready-for-pickup`, {});
      setOk("Ordine segnato come Pronto per il ritiro. Email inviata al cliente.");
      setTimeout(() => onDone?.(), 700);
    } catch (e) {
      const raw = e?.data?.detail?.code || e?.data?.detail || e?.message || String(e);
      setErr(humanErr(raw));
    } finally { setBusy(false); }
  };

  return (
    <Section title="Ritiro cliente"
             subtitle="Ordine PICKUP — non entra nel Piano Carichi, non prevede vettore">
      <Card data-testid="pickup-ready-card"><Card.Body>
        {err && <Alert tone="danger" data-testid="pickup-error">{err}</Alert>}
        {ok && <Alert tone="success" data-testid="pickup-ok">{ok}</Alert>}

        {loc && (
          <Card style={{ background: "#F3F6FA", marginBottom: 12 }}
                data-testid="pickup-location-info">
            <Card.Body style={{ padding: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Sede di ritiro</div>
              {loc.warehouse_name && <div><strong>{loc.warehouse_name}</strong></div>}
              {loc.address && <div>{loc.address}</div>}
              {(loc.zip_code || loc.city) && (
                <div>{loc.zip_code} {loc.city}{loc.province ? ` (${loc.province})` : ""}</div>
              )}
              {loc.phone && <div>Tel. {loc.phone}</div>}
              {loc.pickup_hours && (
                <div className="muted" style={{ fontSize: ".85rem", marginTop: 4 }}>
                  Orari ritiro: {loc.pickup_hours}
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        <div style={{ marginBottom: 12 }}>
          <Label>Stato DDT</Label>
          <div data-testid="pickup-ddt-state">
            {hasDdt
              ? <span style={{ color: "#15803d", fontWeight: 600 }}>DDT caricato</span>
              : <span style={{ color: "#b45309", fontWeight: 600 }}>Nessun DDT caricato</span>}
          </div>
        </div>

        {!hasDdt && (
          <Card style={{ background: "#f8fafc", marginBottom: 12 }}>
            <Card.Body>
              {/* v43.32 — Modalità 1: auto-lookup dal registro Zucchetti +
                  fallback cartella. Non attacca mai silenziosamente: 1 match
                  richiede click "Allega DDT trovato", multi-match richiede
                  scelta esplicita. Riusa GET /ddt/search + POST
                  /ddt/attach-from-folder (già in prod). */}
              {!lookupDisabled && (
                <div data-testid="pickup-ddt-lookup-block"
                     style={{ marginBottom: 14, padding: 10, border: "1px solid #e2e8f0",
                              borderRadius: 6, background: "#fff" }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>
                    Modalità 1 — Cerca DDT dal registro Zucchetti
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "flex-end",
                                flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 200px" }}>
                      <Label>Numero DDT (es. 2382/2026)</Label>
                      <Input value={ddtNumber}
                             onChange={(e) => { setDdtNumber(e.target.value);
                                                setLookup(null); setLookupErr(null); }}
                             data-testid="pickup-ddt-lookup-input" />
                    </div>
                    <Button size="sm" variant="primary"
                            disabled={lookupBusy || !ddtNumber.trim()}
                            onClick={runLookup}
                            data-testid="pickup-ddt-lookup-search-btn">
                      {lookupBusy ? "Cerco…" : "Cerca DDT"}
                    </Button>
                  </div>
                  {lookupErr && (
                    <Alert tone="danger" style={{ marginTop: 8 }}
                           data-testid="pickup-ddt-lookup-err">{lookupErr}</Alert>
                  )}
                  {lookup?.status === "not_found" && (
                    <Alert tone="info" style={{ marginTop: 8 }}
                           data-testid="pickup-ddt-lookup-not-found">
                      Nessun DDT trovato automaticamente per <strong>{lookup.normalized}</strong>.
                      Usa la Modalità 2 (upload manuale) qui sotto.
                    </Alert>
                  )}
                  {lookup?.status === "found" && lookup.matches?.[0] && (
                    <Alert tone="success" style={{ marginTop: 8 }}
                           data-testid="pickup-ddt-lookup-found">
                      DDT trovato: <strong>{lookup.matches[0].filename}</strong>
                      <div style={{ marginTop: 8 }}>
                        <Button size="sm" variant="primary" disabled={lookupBusy}
                                onClick={() => attachFromRegistry(lookup.matches[0])}
                                data-testid="pickup-ddt-lookup-attach-btn">
                          {lookupBusy ? "Allego…" : "Allega DDT trovato"}
                        </Button>
                      </div>
                    </Alert>
                  )}
                  {lookup?.status === "conflict" && (
                    <Alert tone="warning" style={{ marginTop: 8 }}
                           data-testid="pickup-ddt-lookup-conflict">
                      Trovati {lookup.matches.length} file compatibili — scegli manualmente:
                      <ul style={{ margin: "6px 0 0 18px" }}>
                        {lookup.matches.map((m) => (
                          <li key={m.filename} style={{ marginTop: 4 }}>
                            <code>{m.filename}</code>{" "}
                            <Button size="sm" variant="outline" disabled={lookupBusy}
                                    onClick={() => attachFromRegistry(m)}
                                    data-testid={`pickup-ddt-lookup-attach-${m.filename}`}>
                              Allega
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                </div>
              )}

              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Modalità 2 — Carica il DDT manualmente
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                            gap: 12 }}>
                <div>
                  <Label>Numero DDT</Label>
                  <Input value={ddtNumber} onChange={(e) => setDdtNumber(e.target.value)}
                         placeholder="Es. 2026/1234"
                         data-testid="pickup-ddt-number" />
                </div>
                <div>
                  <Label>Data DDT</Label>
                  <Input type="date" value={ddtDate} onChange={(e) => setDdtDate(e.target.value)}
                         data-testid="pickup-ddt-date" />
                </div>
                <div>
                  <Label>File PDF</Label>
                  <input type="file" accept="application/pdf"
                         onChange={(e) => setDdtFile(e.target.files?.[0] || null)}
                         data-testid="pickup-ddt-file"
                         style={{ display: "block" }} />
                </div>
                <div>
                  <Label>Tipo DDT</Label>
                  <select className="ds-input" value={ddtKind}
                          onChange={(e) => setDdtKind(e.target.value)}
                          data-testid="pickup-ddt-kind">
                    <option value="STANDARD">DDT standard</option>
                    <option value="SAMPLE">DDT campionatura</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <Button size="sm" onClick={uploadDdt} disabled={ddtBusy}
                        data-testid="pickup-ddt-upload-btn">
                  {ddtBusy ? "Caricamento…" : "Carica DDT"}
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="primary" onClick={markReady} loading={busy} disabled={busy}
                  data-testid="btn-pickup-ready">
            {busy ? "Attendi…" : "Pronto per il ritiro"}
          </Button>
        </div>
      </Card.Body></Card>
    </Section>
  );
}

function ShipmentActionsInline({ entry, onDone }) {
  const { apiPost, apiPatch, token } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ddtFile, setDdtFile] = useState(null);
  const [ddtNumber, setDdtNumber] = useState("");
  const [ddtKind, setDdtKind] = useState("STANDARD");
  const [ok, setOk] = useState(null);
  const status = entry.shipment_status;
  const alreadyDelivered = status === "DELIVERED";

  const doMarkDeparted = async () => {
    setErr(null); setBusy(true);
    try {
      await apiPost(`/api/shipments/${entry.id}/mark-departed`, {});
      setOk("Partenza confermata — ordine → SHIPPING");
      onDone?.();
    } catch (e) {
      setErr(humanErr(e?.response?.data?.detail || String(e)));
    } finally { setBusy(false); }
  };
  const doMarkDelivered = async () => {
    setErr(null); setOk(null);
    if (!window.confirm(
        `Confermi che la spedizione ${entry.number} risulta consegnata?`)) return;
    setBusy(true);
    try {
      const res = await apiPost(`/api/shipments/${entry.id}/mark-delivered`, {});
      const orderMsg = res.order_status === "DELIVERED"
        ? "Ordine completamente evaso → passa a Consegnati."
        : "Ordine ha ancora residui aperti → resta in Spedizione.";
      const tplLabel = {
        shipment_delivered_complete: "Email consegna completa inviata al cliente.",
        shipment_delivered_partial_open: "Email consegna parziale (residuo aperto) inviata al cliente.",
        shipment_delivered_partial_closed: "Email consegna parziale (residuo chiuso) inviata al cliente.",
      }[res.template] || "Cliente notificato.";
      const emailMsg = res.email_skipped_reason === "customer_no_email"
        ? "Cliente senza email — notifica saltata (audit registrato)."
        : res.idempotent_replay
        ? "Spedizione già registrata come consegnata — nessuna nuova email."
        : tplLabel;
      setOk(`Consegna registrata. ${orderMsg} ${emailMsg}`);
      onDone?.();
    } catch (e) {
      setErr(humanErr(e?.data?.detail || e?.message || String(e)));
    } finally { setBusy(false); }
  };
  const doEtaUpdate = async () => {
    setErr(null); setBusy(true);
    try {
      // Fix #ETA-401: usa apiPatch (auth client centralizzato) invece di
      // fetch raw + `Bearer ${token}` che, quando il token è null/undefined
      // (sessione scaduta o non caricata), spediva letteralmente
      // "Bearer null" / "Bearer undefined" facendo restituire al backend
      // il 401 grezzo `{"detail":"Invalid or expired token"}` mostrato al
      // cliente. apiPatch salta l'header se il token è falsy → il resto
      // dell'app gestisce già il redirect a login su 401.
      await apiPatch(`/api/shipments/${entry.id}/expected-delivery-date`, {});
      setOk("Data stimata ricalcolata dalle regole del vettore");
      onDone?.();
    } catch (e) {
      setErr(humanErr(e?.data?.detail || e?.message || String(e)));
    } finally { setBusy(false); }
  };
  const doDdtReplace = async () => {
    setErr(null); setBusy(true);
    try {
      if (!ddtFile) throw new Error("Seleziona un PDF");
      const fd = new FormData();
      if (ddtNumber) fd.append("ddt_number", ddtNumber);
      fd.append("kind", ddtKind);
      fd.append("file", ddtFile);
      const res = await fetch(`${API}/api/shipments/${entry.id}/ddt`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`DDT upload ${res.status}: ${await res.text()}`);
      setOk("DDT caricato");
      onDone?.();
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 8 }} data-testid={`shipactions-${entry.number}`}>
      <Button size="sm" onClick={() => setOpen(!open)} data-testid={`shipactions-toggle-${entry.number}`}>
        {open ? "Chiudi azioni" : "Azioni ↓"}
      </Button>
      {open && (
        <Card style={{ marginTop: 6, background: "#f8fafc" }}><Card.Body>
          {err && <Alert tone="danger">{err}</Alert>}
          {ok && <Alert tone="success" data-testid={`shipactions-ok-${entry.number}`}>{ok}</Alert>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
            <div>
              <Label>Data stimata di consegna</Label>
              <div data-testid={`eta-${entry.number}`}>
                {entry.expected_delivery_date ? fmtDate(entry.expected_delivery_date) : "Data di consegna in aggiornamento"}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                <Button size="sm" variant="primary" loading={busy} disabled={busy || alreadyDelivered}
                        data-testid={`btn-mark-departed-${entry.number}`}
                        onClick={doMarkDeparted}>Conferma partenza</Button>
                <Button size="sm" loading={busy} disabled={busy || alreadyDelivered}
                        data-testid={`btn-eta-${entry.number}`}
                        onClick={doEtaUpdate}>Ricalcola data stimata</Button>
              </div>
              {status === "IN_TRANSIT" && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed #cbd5e1" }}>
                  <Button size="sm" variant="primary" loading={busy} disabled={busy}
                          data-testid={`btn-mark-delivered-${entry.number}`}
                          onClick={doMarkDelivered}
                          style={{ background: "#059669", borderColor: "#059669" }}>
                    ✓ Segna consegnata
                  </Button>
                  <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                    Notifica cliente via email · rispetta il tuo perimetro Logistics/Admin.
                  </div>
                </div>
              )}
              {alreadyDelivered && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed #cbd5e1",
                              fontSize: 12, color: "#059669" }}
                     data-testid={`already-delivered-${entry.number}`}>
                  ✓ Spedizione già consegnata
                </div>
              )}
            </div>
            <div>
              <Label>Nuovo DDT PDF (sostituzione)</Label>
              <Input placeholder="Numero DDT (opzionale)" value={ddtNumber}
                     data-testid={`ddt-num-${entry.number}`}
                     onChange={(e) => setDdtNumber(e.target.value)} />
              <div style={{ marginTop: 6 }}>
                <Label>Tipo DDT</Label>
                <select className="ds-input" value={ddtKind}
                        onChange={(e) => setDdtKind(e.target.value)}
                        data-testid={`ddt-kind-${entry.number}`}>
                  <option value="STANDARD">DDT standard</option>
                  <option value="SAMPLE">DDT campionatura</option>
                </select>
              </div>
              <input type="file" accept="application/pdf"
                     data-testid={`ddt-file-${entry.number}`}
                     onChange={(e) => setDdtFile(e.target.files?.[0] || null)}
                     style={{ marginTop: 6, display: "block" }} />
              <Button size="sm" style={{ marginTop: 6 }} loading={busy} disabled={busy || !ddtFile}
                      data-testid={`btn-ddt-upload-${entry.number}`}
                      onClick={doDdtReplace}>Carica / Sostituisci DDT</Button>
            </div>
          </div>
        </Card.Body></Card>
      )}
    </div>
  );
}

const CardRow = memo(function CardRow({ entry, tab, selectMode = false, selected = false, onToggle }) {
  const isShipment = entry.kind === "shipment";
  const status = isShipment ? entry.shipment_status : entry.status;
  const statusLabel = isShipment ? (SHIP_STATUS_IT[status] || status)
                                 : (ORDER_STATUS_IT[status] || status);
  const statusTone = status === "DELIVERED" ? "success"
                   : status === "IN_TRANSIT" ? "info"
                   : status === "IN_DELIVERY" ? "info"
                   : status === "SHIPPING" ? "info"
                   : status === "PROCESSING" ? "warning"
                   : status === "RECEIVED" ? "info" : "neutral";
  const linkTo = isShipment
    ? (entry.load_plan_id ? `/logistics/load-plans/${entry.load_plan_id}` : `/logistics/ship/${entry.order_id}`)
    : (tab === "ricevuti"
        ? `/logistics/order/${entry.id}`   // v34.30 — canonical read-only detail
        : `/logistics/ship/${entry.id}`);   // preparation wizard for da_preparare tab
  return (
    <Card data-testid={`log-card-${entry.number}`}>
      <Card.Body>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: "1 1 220px" }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              {/* Mar 2026 — checkbox multi-select solo su RECEIVED e solo in selectMode */}
              {selectMode && tab === "ricevuti" && entry.status === "RECEIVED" && (
                <input type="checkbox" checked={selected}
                       onChange={() => onToggle && onToggle(entry.id)}
                       data-testid={`log-select-${entry.number}`}
                       style={{ marginRight: 6, transform: "scale(1.1)" }} />
              )}
              <strong>{entry.number}</strong>
              {isShipment && entry.order_number && (
                <span className="muted" style={{ fontSize: ".78rem" }}>· ord. {entry.order_number}</span>
              )}
              {entry.is_residual && (
                <Badge tone="warning" data-testid={`log-residual-badge-${entry.number}`}
                       style={{ fontWeight: 700, letterSpacing: ".3px" }}>
                  STRALCIO RESIDUO
                </Badge>
              )}
              <Badge tone={statusTone}>{statusLabel}</Badge>
              {isShipment && entry.load_plan_status && (
                <Badge tone="neutral">Piano {entry.load_plan_status === "DRAFT" ? "bozza" : "inviato"}</Badge>
              )}
              {isShipment && !entry.has_ddt && (status === "IN_TRANSIT" || status === "DELIVERED") && (
                <Badge tone="danger">DDT mancante</Badge>
              )}
            </div>
            {entry.is_residual && (
              <div className="muted" data-testid={`log-residual-lineage-${entry.number}`}
                   style={{ fontSize: ".78rem", marginTop: 2, color: "#92400E" }}>
                Deriva da <strong>{entry.residual_of_order_number || "—"}</strong> · residuo da evadere
              </div>
            )}
            <div style={{ marginTop: 4 }}>
              <strong>{entry.customer_name}</strong>{" "}
              <span className="muted" style={{ fontSize: ".78rem" }}>{entry.customer_code}</span>
            </div>
            <div className="muted" style={{ fontSize: ".82rem", marginTop: 2, display: "flex",
                                             alignItems: "center", gap: 4, flexWrap: "wrap" }}>
              <MapPin size={12} />
              {entry.dp_name ? (
                <>
                  {entry.dp_name} · {entry.dp_city} ({entry.dp_province}) · {entry.dp_region}
                </>
              ) : (
                <em>Ritiro cliente</em>
              )}
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: ".82rem" }}>
            <div>
              <Layers size={12} style={{ verticalAlign: "-2px" }} />{" "}
              <strong data-testid={`log-pallets-${entry.number}`}>{fmtPallets(entry.estimated_pallets)}</strong> pallet ·{" "}
              <strong>{fmtKg(entry.total_weight_kg)}</strong> kg
            </div>
            {entry.received_at && (
              <div data-testid={`log-received-${entry.number}`}
                   style={{ marginTop: 4, color: "#0F4C81", fontWeight: 600 }}>
                <Clock size={12} style={{ verticalAlign: "-2px" }} /> Ricevuto: {fmtDateTime(entry.received_at)}
              </div>
            )}
            <div className="muted" style={{ marginTop: 2 }}>
              {entry.requested_delivery_date && (
                <><Calendar size={12} style={{ verticalAlign: "-2px" }} /> Consegna richiesta: {fmtDate(entry.requested_delivery_date)}</>
              )}
            </div>
            {entry.confirmed_delivery_date && (
              <div className="muted">Conferm.: {fmtDate(entry.confirmed_delivery_date)}</div>
            )}
            {isShipment && entry.expected_delivery_date && (
              <div className="muted">Prevista: {fmtDate(entry.expected_delivery_date)}</div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
          {entry.truck_accessible === true && <Badge tone="success">Bilico: SI</Badge>}
          {entry.truck_accessible === false && <Badge tone="danger">Bilico: NO</Badge>}
          {(entry.truck_accessible === null || entry.truck_accessible === undefined) && <Badge tone="neutral">Bilico: DA VERIFICARE</Badge>}
          {entry.tail_lift_required === true && <Badge tone="warning">Sponda: SI</Badge>}
          {entry.tail_lift_required === false && <Badge tone="neutral">Sponda: NO</Badge>}
          {(entry.tail_lift_required === null || entry.tail_lift_required === undefined) && <Badge tone="neutral">Sponda: DA VERIFICARE</Badge>}
          {isShipment && entry.carrier_name && (
            <Badge tone="info">Vettore: {entry.carrier_name}</Badge>
          )}
          <div style={{ marginLeft: "auto" }}>
            <Link to={linkTo} state={{ fromTab: tab }}>
              <Button size="sm" variant="primary"
                      data-testid={`log-open-${entry.number}`}>
                {tab === "ricevuti" ? "Apri dettaglio →"
                 : tab === "da_preparare" ? "Prepara spedizione →"
                 : tab === "in_piano" ? "Apri piano →"
                 : "Apri →"}
              </Button>
            </Link>
          </div>
        </div>
        {isShipment && tab === "in_consegna" && (status === "IN_TRANSIT" || status === "DRAFT" || status === "DELIVERED") && (
          <ShipmentActionsInline entry={entry} onDone={() => window.location.reload()} />
        )}
        {!isShipment && tab === "ricevuti" && status === "RECEIVED" && (
          <ReceivedActionsInline entry={entry} onDone={() => window.location.reload()} />
        )}
      </Card.Body>
    </Card>
  );
});

export function LogisticsHome() {
  const { apiGet, token } = useAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  // Feb 2026 — se veniamo qui via back contestuale dal wizard, ripristina
  // il tab d'origine (location.state.activeTab). Fallback: query param
  // ?tab= (utile per link condivisi). Default: "ricevuti".
  const _initTab = (() => {
    const fromState = location.state?.activeTab;
    if (fromState) return fromState;
    const sp = new URLSearchParams(location.search);
    const t = sp.get("tab");
    return t || "ricevuti";
  })();
  const [tab, setTab] = useState(_initTab);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dateField, setDateField] = useState("requested");
  const [err, setErr] = useState(null);
  // Mar 2026 — Sort + multi-select RECEIVED archive.
  const [sortKey, setSortKey] = useState("received_desc");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [archiving, setArchiving] = useState(false);
  // v43.35.9 — reset selection on tab/filter changes (NOT on sortKey: pure
  // client-side sorting must not clear a running multi-select).
  useEffect(() => { setSelectMode(false); setSelectedIds(new Set()); },
    [tab, q, region, dateFrom, dateTo, dateField]);

  const buildQuery = (extra = "") => {
    let url = `/api/logistics/home?tab=${tab}` +
              (q ? `&q=${encodeURIComponent(q)}` : "") +
              (region ? `&region=${encodeURIComponent(region)}` : "") +
              (dateFrom ? `&date_from=${dateFrom}` : "") +
              (dateTo ? `&date_to=${dateTo}` : "") +
              (dateField ? `&date_field=${dateField}` : "");
    return url + extra;
  };

  const load = async () => {
    setErr(null);
    // v43.35.9 — latest-wins guard. Every load() gets a monotonic seq;
    // when the response comes back we drop it if a newer load() was fired
    // meanwhile (e.g. debounced filter response arriving after a fresher
    // tab click). Prevents "stale filter payload overwriting fresh tab
    // payload" and vice versa.
    const my = ++_reqSeq.current;
    try {
      const r = await apiGet(buildQuery());
      if (my === _reqSeq.current) setData(r);
    } catch (e) {
      if (my === _reqSeq.current) setErr(e?.message || String(e));
    }
  };

  // v43.35.9 — Single fetch coordinator with tab-vs-filter routing.
  //   - initial mount        → immediate load
  //   - tab change            → immediate load
  //   - q/region/date changes → debounced 300 ms
  //   - sort / selection      → 0 requests (client-side)
  // The previous coordinator naively debounced EVERY dep, so switching
  // tab took 300 ms to feel any response. Now we compare tab to its
  // previous value inside the effect and skip the timer for tab-only deltas.
  const _mounted = useRef(false);
  const _prevTab = useRef(tab);
  const _reqSeq = useRef(0);
  useEffect(() => {
    if (!_mounted.current) {
      _mounted.current = true;
      _prevTab.current = tab;
      load();
      return;
    }
    if (tab !== _prevTab.current) {
      _prevTab.current = tab;
      load();
      return;
    }
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [tab, q, region, dateFrom, dateTo, dateField]);

  const applyPreset = (preset) => {
    if (preset === "today") { setDateFrom(isoToday()); setDateTo(isoToday()); }
    else if (preset === "tomorrow") { const t = isoDaysFromToday(1); setDateFrom(t); setDateTo(t); }
    else if (preset === "this_week") { const [a, b] = weekBounds(0); setDateFrom(a); setDateTo(b); }
    else if (preset === "next_week") { const [a, b] = weekBounds(1); setDateFrom(a); setDateTo(b); }
  };

  const exportXlsx = async () => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const url = `${backend}/api/logistics/home/export.xlsx?tab=${tab}` +
                (q ? `&q=${encodeURIComponent(q)}` : "") +
                (region ? `&region=${encodeURIComponent(region)}` : "") +
                (dateFrom ? `&date_from=${dateFrom}` : "") +
                (dateTo ? `&date_to=${dateTo}` : "") +
                `&date_field=${dateField}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token || ""}` } });
    if (!res.ok) { alert("Export fallito: " + await res.text()); return; }
    const blob = await res.blob();
    const dlUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = dlUrl; a.download = `LogisticsHome_${tab}_${new Date().toISOString().slice(0,10)}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(dlUrl);
  };

  const kpis = data?.kpis || {};
  const rawItems = data?.buckets?.[tab] || [];
  // Mar 2026 — Sort client-side sul dataset già filtrato dal backend.
  const items = useMemo(() => {
    const arr = [...rawItems];
    const numAsc = (a, b) => String(a.number || "").localeCompare(String(b.number || ""), "it", { numeric: true });
    const cliAsc = (a, b) => String(a.customer_name || "").localeCompare(String(b.customer_name || ""), "it");
    const dReq = (x) => x.requested_delivery_date || x.expected_delivery_date || "9999-12-31";
    const dRec = (x) => x.created_at || x.received_at || "";
    switch (sortKey) {
      case "received_asc": arr.sort((a, b) => String(dRec(a)).localeCompare(String(dRec(b)))); break;
      case "number_asc": arr.sort(numAsc); break;
      case "number_desc": arr.sort((a, b) => numAsc(b, a)); break;
      case "customer_asc": arr.sort(cliAsc); break;
      case "customer_desc": arr.sort((a, b) => cliAsc(b, a)); break;
      case "delivery_asc": arr.sort((a, b) => String(dReq(a)).localeCompare(String(dReq(b)))); break;
      case "delivery_desc": arr.sort((a, b) => String(dReq(b)).localeCompare(String(dReq(a)))); break;
      case "status": arr.sort((a, b) => String(a.status || "").localeCompare(String(b.status || ""))); break;
      case "received_desc":
      default: arr.sort((a, b) => String(dRec(b)).localeCompare(String(dRec(a))));
    }
    return arr;
  }, [rawItems, sortKey]);

  const receivedIdsVisible = useMemo(
    () => items.filter(x => x.status === "RECEIVED").map(x => x.id),
    [items],
  );
  // v43.35.9 — stable callback for CardRow (React.memo prop equality).
  // Recreating this on every render would defeat the memoization.
  const toggleSel = useCallback((id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);
  const selectAllVisible = () => setSelectedIds(new Set(receivedIdsVisible));
  const clearSel = () => setSelectedIds(new Set());
  const archiveSelected = async () => {
    if (archiving || selectedIds.size === 0) return;
    if (!window.confirm(`Vuoi rimuovere ${selectedIds.size} ordini ricevuti dalle liste operative?`)) return;
    setArchiving(true); setErr(null);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/archive-received`, {
        method: "POST",
        headers: { "Content-Type": "application/json",
                   Authorization: `Bearer ${token || localStorage.getItem("access_token") || ""}` },
        body: JSON.stringify({ order_ids: [...selectedIds] }),
      });
      const j = await r.json();
      if (!r.ok) { setErr(j?.detail || "Errore archiviazione"); return; }
      const rejected = (j.items || []).filter(i => !i.archived);
      if (rejected.length) {
        alert(`Archiviati: ${j.archived_count}\nRifiutati: ${rejected.length}\n` +
              rejected.map(x => `• ${x.number || x.order_id}: ${x.reason}`).join("\n"));
      }
      clearSel(); setSelectMode(false);
      await load();
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setArchiving(false); }
  };

  const regions = useMemo(() => {
    const rs = new Set();
    Object.values(data?.buckets || {}).forEach((arr) => arr.forEach((e) => e.dp_region && rs.add(e.dp_region)));
    return [...rs].sort();
  }, [data]);

  return (
    <>
      <EmailImportPendingCard />
      <Section title="Dashboard Logistica"
               subtitle="Preparazione, pianificazione e consegna delle spedizioni"
               actions={
                 <Link to="/logistics/load-plans">
                   <Button variant="primary" size="lg" data-testid="btn-goto-loadplans">
                     <Truck size={16} /> Piano Carichi
                   </Button>
                 </Link>
               }
               data-testid="logistics-home">
        {err && <Alert tone="danger">Errore: {err}</Alert>}

        {/* TABS */}
        <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #e5e7eb",
                      marginBottom: 12, flexWrap: "wrap" }} role="tablist"
             data-testid="logistics-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} role="tab"
                    data-testid={`tab-${t.id}`}
                    aria-selected={tab === t.id}
                    style={{ padding: "8px 14px", border: "none", cursor: "pointer",
                             borderBottom: tab === t.id ? "3px solid #0F4C81" : "3px solid transparent",
                             background: "transparent",
                             fontWeight: tab === t.id ? 700 : 500,
                             color: tab === t.id ? "#0F4C81" : "#475569" }}>
              {t.label}{t.kpiKey && kpis[t.kpiKey] != null && ` (${kpis[t.kpiKey]})`}
            </button>
          ))}
        </div>

        {/* FILTERS BAR — compact single row with explicit Filtra/Pulisci */}
        <Card style={{ marginBottom: 12 }} data-testid="logistics-filters">
          <Card.Body style={{ padding: 12 }}>
            <div style={{ display: "grid",
                          gridTemplateColumns: "minmax(220px,2fr) minmax(140px,1fr) minmax(180px,1fr) 140px 140px auto",
                          gap: 8, alignItems: "flex-end" }}>
              <div>
                <Label htmlFor="log-search">Ricerca</Label>
                <div style={{ position: "relative" }}>
                  <Search size={14} style={{ position: "absolute", left: 8, top: 12, color: "#94a3b8" }} />
                  <Input id="log-search" data-testid="log-search-input"
                         value={q} onChange={(e) => setQ(e.target.value)}
                         onKeyDown={(e) => e.key === "Enter" && load()}
                         placeholder="Ordine, cliente, codice, agente, città…"
                         style={{ paddingLeft: 28 }} />
                </div>
              </div>
              <div>
                <Label htmlFor="log-region">Regione</Label>
                <select id="log-region" data-testid="log-region-select"
                        value={region} onChange={(e) => setRegion(e.target.value)}
                        style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}>
                  <option value="">Tutte</option>
                  {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label>Campo data</Label>
                <select value={dateField} onChange={(e) => setDateField(e.target.value)}
                        data-testid="log-date-field"
                        style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" }}>
                  {DATE_FIELDS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                </select>
              </div>
              <div>
                <Label>Da</Label>
                <Input type="date" value={dateFrom}
                       data-testid="log-date-from"
                       onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div>
                <Label>A</Label>
                <Input type="date" value={dateTo}
                       data-testid="log-date-to"
                       onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <Button size="sm" variant="primary" onClick={load}
                        data-testid="log-apply-filters">
                  <Search size={14} /> Filtra
                </Button>
                <Button size="sm" variant="secondary"
                        onClick={() => { setQ(""); setRegion(""); setDateFrom(""); setDateTo(""); }}
                        data-testid="log-clear-filters">
                  <XCircle size={14} /> Pulisci
                </Button>
              </div>
            </div>

            {/* Date presets — subtle second row */}
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap",
                          alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "#64748b" }}>Preset:</span>
              <Button size="sm" onClick={() => applyPreset("today")} data-testid="preset-today">Oggi</Button>
              <Button size="sm" onClick={() => applyPreset("tomorrow")} data-testid="preset-tomorrow">Domani</Button>
              <Button size="sm" onClick={() => applyPreset("this_week")} data-testid="preset-this-week">Questa sett.</Button>
              <Button size="sm" onClick={() => applyPreset("next_week")} data-testid="preset-next-week">Prossima sett.</Button>
              {/* Mar 2026 — Ordina + Seleziona ordini + Rimuovi (RECEIVED only) */}
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginLeft: 12 }}>
                <span style={{ fontSize: 12, color: "#64748b" }}>Ordina:</span>
                <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}
                        data-testid="log-sort-select"
                        style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 13 }}>
                  <option value="received_desc">Ricezione: più recenti</option>
                  <option value="received_asc">Ricezione: più vecchi</option>
                  <option value="number_asc">Numero ordine ↑</option>
                  <option value="number_desc">Numero ordine ↓</option>
                  <option value="customer_asc">Cliente A→Z</option>
                  <option value="customer_desc">Cliente Z→A</option>
                  <option value="delivery_asc">Consegna: più vicina</option>
                  <option value="delivery_desc">Consegna: più lontana</option>
                  <option value="status">Stato</option>
                </select>
              </div>
              {tab === "ricevuti" && (
                <Button size="sm"
                        variant={selectMode ? "primary" : "secondary"}
                        onClick={() => { setSelectMode(v => !v); clearSel(); }}
                        data-testid="log-select-mode-toggle">
                  {selectMode ? "Annulla selezione" : "Seleziona ordini"}
                </Button>
              )}
              {selectMode && tab === "ricevuti" && (
                <>
                  <Button size="sm" onClick={selectAllVisible}
                          data-testid="log-select-all-received"
                          disabled={receivedIdsVisible.length === 0}>
                    Seleziona tutti i RECEIVED visibili ({receivedIdsVisible.length})
                  </Button>
                  <Button size="sm" variant="danger"
                          loading={archiving} disabled={archiving || selectedIds.size === 0}
                          onClick={archiveSelected}
                          data-testid="log-archive-selected">
                    Rimuovi selezionati ({selectedIds.size})
                  </Button>
                </>
              )}
              <div style={{ marginLeft: "auto", fontSize: 12, color: "#64748b" }}
                   data-testid="log-search-scope-hint">
                Ricerca limitata al tab «{TABS.find((t) => t.id === tab)?.label}».
                Gli ordini <strong>RECEIVED</strong> non presi in carico non compaiono in Logistica.
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* EXPORT — visually separated from filters */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
          <Button size="sm" variant="secondary" onClick={exportXlsx}
                  data-testid="log-export-xlsx">
            <Download size={14} /> Export XLSX
          </Button>
        </div>

        {/* LIST */}
        {!data ? (
          <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>
        ) : items.length === 0 ? (
          <Alert tone="info" data-testid="logistics-empty">
            Nessun elemento in questo bucket con i filtri attuali.
          </Alert>
        ) : (
          <div style={{ display: "grid", gap: 10 }} data-testid="logistics-list">
            {items.map((e) => (
              <CardRow key={`${e.kind}-${e.id}`} entry={e} tab={tab}
                       selectMode={selectMode}
                       selected={selectedIds.has(e.id)}
                       onToggle={toggleSel} />
            ))}
          </div>
        )}
      </Section>
    </>
  );
}


function CloseResidualButton({ lineId, sku, maxQty }) {
  const { apiPost } = useAuth();
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("CUSTOMER_REQUEST");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      const body = { closed_qty_cartons: Number(qty), close_reason: reason };
      if (reason === "OTHER") body.close_reason_note = note;
      else if (note) body.internal_note = note;
      await apiPost(`/api/order-lines/${lineId}/close-residual`, body);
      setMsg("ok");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      setMsg(e?.response?.data?.detail || String(e));
    } finally { setBusy(false); }
  };

  if (maxQty <= 0) return <span className="muted">—</span>;
  if (!open) {
    return <Button size="sm" data-testid={`close-res-btn-${sku}`}
                   onClick={() => setOpen(true)}>Chiudi residuo</Button>;
  }
  const submitDisabled = busy || !qty || (reason === "OTHER" && !note);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 200 }}
         data-testid={`close-res-form-${sku}`}>
      <Input type="number" min="1" max={maxQty} placeholder={`max ${maxQty}`}
             value={qty} onChange={(e) => setQty(e.target.value)}
             data-testid={`close-res-qty-${sku}`} />
      <select className="ds-input" value={reason} onChange={(e) => setReason(e.target.value)}
              data-testid={`close-res-reason-${sku}`}>
        <option value="CUSTOMER_REQUEST">Richiesta cliente</option>
        <option value="SUBSTITUTION_REFUSED">Sostituzione non accettata</option>
        <option value="NOT_NEEDED">Articolo non più necessario</option>
        <option value="OTHER">Altro (nota obbligatoria)</option>
      </select>
      <Input placeholder={reason === "OTHER" ? "Motivo (obbligatorio)" : "Nota interna (opzionale)"}
             value={note} onChange={(e) => setNote(e.target.value)}
             data-testid={`close-res-note-${sku}`} />
      <div style={{ display: "flex", gap: 4 }}>
        <Button size="sm" variant="primary" disabled={submitDisabled}
                onClick={submit} data-testid={`close-res-submit-${sku}`}>Chiudi</Button>
        <Button size="sm" onClick={() => setOpen(false)}>×</Button>
      </div>
      {msg && <div style={{ fontSize: 11, color: msg === "ok" ? "#059669" : "#dc2626" }}>{msg}</div>}
    </div>
  );
}


const PALLET_INVALID_MSG = "Inserisci un numero intero di pallet, minimo 1.";
function validatePallets(v) {
  if (v === "" || v === null || v === undefined) return { ok: true, value: null };
  const s = String(v).trim();
  if (!/^\d+$/.test(s)) return { ok: false, err: PALLET_INVALID_MSG };
  const n = Number(s);
  if (!Number.isInteger(n) || n < 1) return { ok: false, err: PALLET_INVALID_MSG };
  return { ok: true, value: n };
}

function StatusBadge({ status }) {
  const label = ORDER_STATUS_IT[status] || status;
  const tone = status === "DELIVERED" ? "success"
             : status === "IN_DELIVERY" ? "info"
             : status === "SHIPPING" ? "info"
             : status === "PROCESSING" ? "warning"
             : status === "WAITING_PAYMENT" ? "danger"
             : status === "RECEIVED" ? "info" : "neutral";
  return <Badge tone={tone} data-testid={`order-status-badge-${status}`}>{label}</Badge>;
}

function fmtDateOnly(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("it-IT");
}
function fmtPallets(n) {
  // Formatta pallet stimati con 2 decimali max, senza .00 inutili.
  // Fallback esplicito a 0 se null/undefined/NaN.
  const v = Number(n);
  if (!Number.isFinite(v)) return "0";
  return v % 1 === 0 ? String(v) : v.toFixed(2).replace(/\.?0+$/, "");
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtHM(t) {
  // Backend returns time as ISO "HH:MM:SS" or "HH:MM". Trim to HH:MM.
  if (!t) return "";
  const s = String(t);
  const m = s.match(/^(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : s;
}

// v43.35.14 — DELIVERY → PICKUP conversion trigger. Rendered inline under
// "Modalità consegna" on the Logistics order detail. Uses the canonical
// POST /api/orders/{id}/convert-to-pickup endpoint. Available to
// ADMIN / SALES_OPERATOR / LOGISTICS_OPERATOR; CUSTOMER never sees the
// order detail page (Protected route guard).
function ConvertToPickupInlineTrigger({ prep, onDone }) {
  const { apiPost, apiGet, user } = useAuth();
  const o = prep.order;
  const roles = new Set((user?.roles) || []);
  const authorized = roles.has("ADMIN") || roles.has("SALES_OPERATOR") || roles.has("LOGISTICS_OPERATOR");
  const alreadyPickup = o?.delivery_mode === "PICKUP";
  const eligibleStatus = o?.status === "RECEIVED" || o?.status === "PROCESSING";
  const activeShipments = (prep.existing_shipments || []).filter(s => s.status !== "VOIDED");
  const anyNonDraft = activeShipments.some(s => s.status !== "DRAFT");
  const hasLoadPlan = !!prep.load_plan?.id;
  const anyDdt = activeShipments.some(s => s.has_ddt);
  const gateBlocker = alreadyPickup ? "already_pickup"
                     : !eligibleStatus ? `status_${o?.status}`
                     : hasLoadPlan ? "load_plan_present"
                     : anyNonDraft ? "shipment_not_reversible"
                     : anyDdt ? "ddt_present"
                     : null;

  const [open, setOpen] = useState(false);
  const [locs, setLocs] = useState([]);
  const [pickupRuleId, setPickupRuleId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    apiGet("/api/pickup-locations").then((rows) => {
      setLocs(rows || []);
      if ((rows || []).length === 1) setPickupRuleId(rows[0].id);
    }).catch(() => setLocs([]));
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  const runDryRun = async () => {
    setErr(null); setBusy(true); setPreview(null);
    try {
      const res = await apiPost(`/api/orders/${o.id}/convert-to-pickup`,
                                  { pickup_rule_id: pickupRuleId, dry_run: true });
      setPreview(res);
    } catch (e) {
      setErr(e?.data?.detail || e?.message || "convert_preview_failed");
    } finally { setBusy(false); }
  };

  const runConvert = async () => {
    setErr(null); setBusy(true);
    try {
      await apiPost(`/api/orders/${o.id}/convert-to-pickup`,
                    { pickup_rule_id: pickupRuleId, dry_run: false });
      setOpen(false);
      onDone?.();
    } catch (e) {
      setErr(e?.data?.detail || e?.message || "convert_failed");
    } finally { setBusy(false); }
  };

  if (!authorized || alreadyPickup) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <Button size="xs" variant="outline"
              disabled={!!gateBlocker}
              data-testid="convert-to-pickup-open"
              onClick={() => setOpen(true)}>
        Converti in ritiro cliente
      </Button>
      {gateBlocker && (
        <div className="muted" data-testid="convert-to-pickup-blocked"
             style={{ fontSize: 11, marginTop: 4 }}>
          {gateBlocker === "load_plan_present" && "Modalità non modificabile: ordine già inserito in un Piano Carichi."}
          {gateBlocker === "shipment_not_reversible" && "Modalità non modificabile: spedizione già attiva."}
          {gateBlocker === "ddt_present" && "Modalità non modificabile: DDT già emesso."}
          {gateBlocker?.startsWith?.("status_") && `Modalità non modificabile: stato ${o?.status}.`}
        </div>
      )}
      {open && (
        <div data-testid="convert-to-pickup-modal"
             style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
                      display: "grid", placeItems: "center", zIndex: 60 }}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8,
                        maxWidth: 560, width: "92%" }}>
            <h3 style={{ marginTop: 0 }}>Converti in ritiro cliente</h3>
            <p>Confermi la conversione dell'ordine <strong>{o?.number}</strong> da
              consegna con vettore a ritiro cliente?</p>

            {locs.length > 1 && (
              <div style={{ margin: "12px 0" }}>
                <Label>Sede di ritiro</Label>
                <select data-testid="convert-to-pickup-rule"
                        value={pickupRuleId || ""}
                        onChange={(e) => setPickupRuleId(e.target.value || null)}
                        style={{ width: "100%", padding: 8 }}>
                  <option value="">— scegli sede —</option>
                  {locs.map(l => (
                    <option key={l.id} value={l.id}>{l.warehouse_name}</option>
                  ))}
                </select>
              </div>
            )}

            {preview && (
              <div data-testid="convert-to-pickup-preview"
                   style={{ background: "#f8fafc", padding: 12, borderRadius: 6,
                            fontSize: 13, marginTop: 10 }}>
                <div><strong>Impatto pricing (ricalcolo automatico):</strong></div>
                <table style={{ width: "100%", marginTop: 6 }}>
                  <thead><tr>
                    <th style={{ textAlign: "left" }}>Voce</th>
                    <th style={{ textAlign: "right" }}>Prima</th>
                    <th style={{ textAlign: "right" }}>Dopo</th>
                  </tr></thead>
                  <tbody>
                    <tr><td>Subtotale</td>
                        <td style={{ textAlign: "right" }}>€ {preview.before.subtotal}</td>
                        <td style={{ textAlign: "right" }}>€ {preview.after.subtotal}</td></tr>
                    <tr><td>Sconti</td>
                        <td style={{ textAlign: "right" }}>€ {preview.before.discount_total}</td>
                        <td style={{ textAlign: "right" }}>€ {preview.after.discount_total}</td></tr>
                    <tr><td>Trasporto</td>
                        <td style={{ textAlign: "right" }}>€ {preview.before.transport_cost}</td>
                        <td style={{ textAlign: "right" }}>€ {preview.after.transport_cost}</td></tr>
                    <tr><td><strong>Totale</strong></td>
                        <td style={{ textAlign: "right" }}><strong>€ {preview.before.total}</strong></td>
                        <td style={{ textAlign: "right" }}><strong>€ {preview.after.total}</strong></td></tr>
                  </tbody>
                </table>
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  Delta totale: <strong>€ {preview.delta_total}</strong> ·
                  Spedizioni da annullare: {preview.shipments_to_void.length}
                </div>
              </div>
            )}

            {err && <Alert tone="danger" data-testid="convert-to-pickup-error">{String(err)}</Alert>}

            <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
              <Button variant="outline" onClick={() => { setOpen(false); setPreview(null); setErr(null); }}
                      data-testid="convert-to-pickup-cancel">Annulla</Button>
              {!preview && (
                <Button variant="primary" loading={busy}
                        disabled={busy || (locs.length > 1 && !pickupRuleId)}
                        onClick={runDryRun}
                        data-testid="convert-to-pickup-preview-btn">
                  Anteprima
                </Button>
              )}
              {preview && (
                <Button variant="primary" loading={busy}
                        onClick={runConvert}
                        data-testid="convert-to-pickup-confirm">
                  Conferma conversione
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function OrderContextSection({ prep, onDone }) {
  const o = prep.order, c = prep.customer, a = prep.agent, dp = prep.delivery_point;
  return (
    <Section title="Dati ordine">
      <Card data-testid="order-ctx-card"><Card.Body>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
          <div>
            <Label>Cliente</Label>
            <div><strong>{c?.business_name || "—"}</strong>{c?.code && <> <span className="muted">({c.code})</span></>}</div>
            {c?.email && <div className="muted" style={{ fontSize: 12 }}>{c.email}</div>}
          </div>
          <div>
            <Label>Agente di riferimento</Label>
            <div>{a ? <><strong>{a.display_name}</strong>{a.code && <> <span className="muted">({a.code})</span></>}</> : <span className="muted">Nessun agente assegnato</span>}</div>
            {a?.email && <div className="muted" style={{ fontSize: 12 }}>{a.email}</div>}
          </div>
          <div>
            <Label>Data ricezione ordine</Label>
            <div>{fmtDateTime(o.received_at)}</div>
          </div>
          <div>
            <Label>Data consegna richiesta</Label>
            <div>{fmtDateOnly(o.requested_delivery_date)}</div>
          </div>
          <div>
            <Label>Data consegna confermata</Label>
            <div>{o.confirmed_delivery_date ? fmtDateOnly(o.confirmed_delivery_date) : <span className="muted">—</span>}</div>
          </div>
          <div data-testid="order-ctx-delivery-mode-cell">
            <Label>Modalità consegna</Label>
            <div data-testid="order-ctx-delivery-mode">{o.delivery_mode}</div>
            <ConvertToPickupInlineTrigger prep={prep} onDone={onDone} />
          </div>
          {o.payment_terms_snapshot && (
            <div>
              <Label>Condizioni pagamento</Label>
              <div style={{ fontSize: 13 }}>{typeof o.payment_terms_snapshot === "object" ? JSON.stringify(o.payment_terms_snapshot) : String(o.payment_terms_snapshot)}</div>
            </div>
          )}
          {o.advance_payment != null && (
            <div>
              <Label>Acconto</Label>
              <div>{o.advance_payment} €</div>
            </div>
          )}
        </div>
        {dp && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed #e5e7eb" }} data-testid="order-ctx-dp">
            <Label>Sede di consegna</Label>
            <div><strong>{dp.name}</strong> — {dp.address}, {dp.zip_code} {dp.city} ({dp.province})</div>
            {(dp.unloading_time_from && dp.unloading_time_to) && (
              <div style={{ marginTop: 4, fontSize: 13 }} data-testid="order-ctx-dp-unloading">
                <span className="muted">ORARI SCARICO:</span>{" "}
                <strong>
                  {(dp.break_from && dp.break_to)
                    ? `${fmtHM(dp.unloading_time_from)}–${fmtHM(dp.break_from)} / ${fmtHM(dp.break_to)}–${fmtHM(dp.unloading_time_to)}`
                    : `${fmtHM(dp.unloading_time_from)}–${fmtHM(dp.unloading_time_to)}`}
                </strong>
              </div>
            )}
            <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="order-ctx-dp-flags">
              <Badge tone={dp.truck_accessible === true ? "success" : dp.truck_accessible === false ? "danger" : "neutral"} data-testid="order-ctx-dp-bilico">
                Bilico: {dp.truck_accessible === true ? "SI" : dp.truck_accessible === false ? "NO" : "DA VERIFICARE"}
              </Badge>
              <Badge tone={dp.tail_lift_required === true ? "warning" : dp.tail_lift_required === false ? "info" : "neutral"} data-testid="order-ctx-dp-sponda">
                Sponda: {dp.tail_lift_required === true ? "SI" : dp.tail_lift_required === false ? "NO" : "DA VERIFICARE"}
              </Badge>
              {dp.booking_required === true && <Badge tone="info">Prenotazione: SI</Badge>}
              {dp.booking_required === false && <Badge tone="neutral">Prenotazione: NO</Badge>}
              {(dp.booking_required === null || dp.booking_required === undefined) && <Badge tone="neutral">Prenotazione: DA VERIFICARE</Badge>}
              {dp.loading_dock_available === true && <Badge tone="info">Banchina: SI</Badge>}
              {dp.loading_dock_available === false && <Badge tone="neutral">Banchina: NO</Badge>}
              {(dp.loading_dock_available === null || dp.loading_dock_available === undefined) && <Badge tone="neutral">Banchina: DA VERIFICARE</Badge>}
              {dp.warehouse_contact_name && <Badge>Ref: {dp.warehouse_contact_name} — {dp.warehouse_contact_phone}</Badge>}
            </div>
            {dp.logistic_notes && <div className="muted" style={{ marginTop: 6 }}>{dp.logistic_notes}</div>}
          </div>
        )}
        {o.notes_customer && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed #e5e7eb" }} data-testid="order-ctx-notes">
            <Label>Note cliente</Label>
            <div style={{ fontStyle: "italic" }}>{o.notes_customer}</div>
          </div>
        )}
      </Card.Body></Card>
    </Section>
  );
}

function OrderLinesReadOnlySection({ lines, order }) {
  // Feb 2026 — Schermata di verifica ordine: economico + logistico read-only.
  // Tutti i valori sono snapshot storici (nessun ricalcolo con regole attuali).
  const fmtEur = (v) => v == null || v === "" ? "—"
    : `€ ${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtEurKg = (v) => v == null || v === "" ? "—"
    : `€ ${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
  // Aggregate applied_promotions from all lines by promo code/name (snapshot storico)
  const aggPromos = new Map();
  for (const l of lines) {
    for (const p of (l.applied_promotions || [])) {
      const key = p.code || p.promotion_code || p.name || p.type || "PROMO";
      const label = p.name || p.description || key;
      const prev = aggPromos.get(key) || { key, label, type: p.type, amount: 0 };
      prev.amount += Number(p.amount || 0);
      aggPromos.set(key, prev);
    }
  }
  const appliedPromosArr = [...aggPromos.values()].filter(p => p.amount > 0);
  const subtotal = order.subtotal != null ? Number(order.subtotal) : null;
  const discountTotal = order.discount_total != null ? Number(order.discount_total) : null;
  const transport = order.transport_cost != null ? Number(order.transport_cost) : null;
  const total = order.total != null ? Number(order.total) : null;
  // Coerenza tecnica: subtotale − sconti + trasporto = totale (con tolleranza 1 cent)
  let anomaly = null;
  if (subtotal != null && discountTotal != null && transport != null && total != null) {
    const expected = subtotal - discountTotal + transport;
    if (Math.abs(expected - total) > 0.01) {
      anomaly = { expected, total };
    }
  }
  const isPickup = order.delivery_mode === "PICKUP";
  return (
    <>
      <Section title="Righe ordine (read-only)">
        <style>{`
          @media (max-width: 900px) {
            [data-testid="order-lines-ro"] .ord-col-logistic { display: none; }
          }
        `}</style>
        <Card data-testid="order-lines-ro"><Card.Body>
          <div style={{ overflowX: "auto" }}>
            <table className="ds-table" data-testid="order-lines-table">
              <thead><tr>
                <th>SKU</th>
                <th>Prodotto</th>
                <th style={{ textAlign: "right" }}>Cartoni</th>
                <th style={{ textAlign: "right" }} className="ord-col-logistic">Kg</th>
                <th style={{ textAlign: "right" }}>€/kg</th>
                <th style={{ textAlign: "right" }}>€/cartone</th>
                <th style={{ textAlign: "right" }}>Sconto riga</th>
                <th style={{ textAlign: "right" }}>Totale riga</th>
                <th style={{ textAlign: "right" }} className="ord-col-logistic">Pallet</th>
              </tr></thead>
              <tbody>
              {lines.map((r) => {
                const isFree = r.line_kind === "FREE";
                const ppk = r.unit_price_effective != null && r.kg_per_carton
                  ? Number(r.unit_price_effective) * Number(r.kg_per_carton) : null;
                return (
                  <tr key={r.order_line_id} data-testid={`ord-line-${r.sku}`}
                      style={isFree ? { background: "#f0fdf4" } : undefined}>
                    <td>{r.sku}</td>
                    <td>
                      {r.name}
                      {isFree && (
                        <span data-testid={`ord-line-free-badge-${r.sku}`}
                              style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 4,
                                       background: "#059669", color: "#fff", fontSize: 10,
                                       fontWeight: 600, letterSpacing: ".03em" }}>OMAGGIO</span>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>{r.qty_ordered}</td>
                    <td style={{ textAlign: "right" }} className="ord-col-logistic">
                      {Number(r.line_weight_kg || 0).toFixed(1)}
                    </td>
                    <td style={{ textAlign: "right" }} data-testid={`ord-line-eurkg-${r.sku}`}>
                      {isFree ? <span className="muted">Omaggio</span> : fmtEurKg(r.unit_price_effective)}
                    </td>
                    <td style={{ textAlign: "right" }} data-testid={`ord-line-eurcart-${r.sku}`}>
                      {isFree ? <span className="muted">€ 0,00</span> : fmtEur(ppk)}
                    </td>
                    <td style={{ textAlign: "right" }} data-testid={`ord-line-disc-${r.sku}`}>
                      {isFree ? <span className="muted">—</span>
                        : (Number(r.line_discount || 0) > 0 ? `− ${fmtEur(r.line_discount)}` : "—")}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}
                        data-testid={`ord-line-total-${r.sku}`}>
                      {isFree ? <span data-testid={`ord-line-omaggio-${r.sku}`}
                                      style={{ color: "#059669", fontWeight: 600 }}>Omaggio</span>
                        : fmtEur(r.line_total)}
                    </td>
                    <td style={{ textAlign: "right" }} className="ord-col-logistic">
                      {r.pallet_calc_snapshot?.pallets_est ?? "—"}
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
          </div>
        </Card.Body></Card>
      </Section>

      <Section title="Riepilogo economico">
        <Card data-testid="order-econ-summary"><Card.Body>
          <div style={{ display: "grid", gap: 6, maxWidth: 480, marginLeft: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Subtotale merce</span>
              <strong data-testid="ord-econ-subtotal">{fmtEur(subtotal)}</strong>
            </div>
            {discountTotal != null && discountTotal > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", color: "#0F7B4A" }}>
                <span>Sconti applicati</span>
                <strong data-testid="ord-econ-discount">− {fmtEur(discountTotal)}</strong>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Spese di trasporto{isPickup && transport === 0 ? " (ritiro in sede)" : ""}</span>
              <strong data-testid="ord-econ-transport">{fmtEur(transport)}</strong>
            </div>
            {isPickup && (
              <div className="muted" style={{ fontSize: ".78rem", textAlign: "right" }}>
                Ritiro in sede — nessun costo di trasporto
              </div>
            )}
            <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 6, paddingTop: 8,
                          display: "flex", justifyContent: "space-between",
                          fontSize: "1.1rem" }}>
              <span><strong>Totale ordine</strong></span>
              <strong data-testid="ord-econ-total" style={{ color: "#0F4C81" }}>
                {fmtEur(total)}
              </strong>
            </div>
          </div>
          {anomaly && (
            <Alert tone="warning" data-testid="ord-econ-anomaly" style={{ marginTop: 10 }}>
              <strong>Anomalia dati storici:</strong>{" "}
              subtotale − sconti + trasporto = {fmtEur(anomaly.expected)},
              ma totale persistito = {fmtEur(anomaly.total)}. Verifica con amministrazione.
            </Alert>
          )}
        </Card.Body></Card>
      </Section>

      {appliedPromosArr.length > 0 && (
        <Section title="Sconti applicati (snapshot storico)">
          <Card data-testid="order-applied-promos"><Card.Body>
            <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
              {appliedPromosArr.map((p, i) => (
                <li key={p.key} data-testid={`ord-promo-${i}`}>
                  <strong>{p.label}</strong>
                  {p.type && <span className="muted" style={{ fontSize: ".78rem" }}> · {p.type}</span>}
                  {": "}
                  <span data-testid={`ord-promo-amount-${i}`}>− {fmtEur(p.amount)}</span>
                </li>
              ))}
            </ul>
            <p className="muted" style={{ fontSize: ".78rem", marginTop: 8, marginBottom: 0 }}>
              Valori congelati al momento della conferma ordine. Non ricalcolati con le regole correnti.
            </p>
          </Card.Body></Card>
        </Section>
      )}

      <Section title="Dati logistici">
        <Card data-testid="order-logistic-summary"><Card.Body>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
            <div><Label>Kg totali</Label>
              <div><strong data-testid="ord-log-kg">{Number(order.total_weight_kg || 0).toFixed(1)} kg</strong></div>
            </div>
            <div><Label>Pallet stimati (totale)</Label>
              <div><strong data-testid="ord-log-pallets">{order.estimated_pallets_total ?? "—"}</strong></div>
            </div>
            <div><Label>Modalità consegna</Label>
              <div><strong>{isPickup ? "Ritiro in sede" : "Consegna a domicilio"}</strong></div>
            </div>
          </div>
        </Card.Body></Card>
      </Section>
    </>
  );
}

function WaitingPaymentBanner({ prep }) {
  const o = prep.order;
  return (
    <Section title="">
      <Alert tone="warning" data-testid="waiting-payment-banner">
        <strong>Ordine sospeso in attesa di verifica amministrativa.</strong>
        <div style={{ marginTop: 4, fontSize: 13 }}>
          Nessuna azione operativa disponibile in questa vista. Attende sblocco da ruolo autorizzato.
          {o.payment_hold_reason && (
            <div style={{ marginTop: 4, color: "#7c2d12" }}>
              Motivo interno: <em>{o.payment_hold_reason}</em>
              {o.payment_hold_at && <> · dal {fmtDateTime(o.payment_hold_at)}</>}
            </div>
          )}
        </div>
      </Alert>
    </Section>
  );
}

/**
 * Feb 2026 — Delivery Estimate (read-only).
 * Fetches GET /api/logistics/shipments/{id}/delivery-estimate via the
 * centralized `apiGet` (silent JWT refresh preserved). Renders the estimate
 * strictly as informative text — non blocking, never overwrites manual dates
 * (expected/confirmed/requested), and never uses words like "garantita",
 * "certa" o "confermata".
 */
function DeliveryEstimateInline({ shipmentId, shipmentNumber }) {
  const { apiGet } = useAuth();
  const [state, setState] = useState({ loading: true, date: null, unavailable: false });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiGet(`/api/logistics/shipments/${shipmentId}/delivery-estimate`);
        if (cancelled) return;
        if (r?.estimated_delivery_date) {
          setState({ loading: false, date: r.estimated_delivery_date, unavailable: false });
        } else {
          setState({ loading: false, date: null, unavailable: true });
        }
      } catch {
        // Non-blocking: any error (incl. 401 that survives silent refresh)
        // degrades silently to "Data stimata non disponibile".
        if (!cancelled) setState({ loading: false, date: null, unavailable: true });
      }
    })();
    return () => { cancelled = true; };
  }, [shipmentId, apiGet]);
  if (state.loading) {
    return (
      <div className="muted" style={{ fontSize: ".78rem", marginTop: 2 }}
           data-testid={`delivery-estimate-loading-${shipmentNumber}`}>
        Calcolo data stimata…
      </div>
    );
  }
  if (state.date) {
    return (
      <div style={{ fontSize: ".78rem", marginTop: 2, color: "#334155" }}
           data-testid={`delivery-estimate-value-${shipmentNumber}`}>
        Data stimata consegna: <strong>{fmtDate(state.date)}</strong>
      </div>
    );
  }
  return (
    <div className="muted" style={{ fontSize: ".78rem", marginTop: 2 }}
         data-testid={`delivery-estimate-unavailable-${shipmentNumber}`}>
      Data stimata non disponibile
    </div>
  );
}

function ExistingShipmentsSection({ shipments, orderId, orderStatus, onEtaUpdated }) {
  const { apiPatch } = useAuth();
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState(null);

  const recalcEta = async (shpId) => {
    setBusyId(shpId); setErr(null);
    try {
      await apiPatch(`/api/shipments/${shpId}/expected-delivery-date`, {});
      onEtaUpdated && onEtaUpdated();
    } catch (e) {
      setErr(humanErr(e?.data?.detail || e?.message || String(e)));
    } finally { setBusyId(null); }
  };

  if (!shipments?.length) {
    return (
      <Section title="Spedizioni collegate">
        <Card><Card.Body className="muted">Nessuna spedizione registrata.</Card.Body></Card>
      </Section>
    );
  }
  const canEditEta = orderStatus === "SHIPPING";
  return (
    <Section title={`Spedizioni collegate (${shipments.length})`}>
      {err && <Alert tone="danger">{err}</Alert>}
      {shipments.map((s) => (
        <Card key={s.id} data-testid={`ship-card-${s.number}`} style={{ marginBottom: 8 }}><Card.Body>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <strong>{s.number}</strong>
            <Badge tone={s.status === "DELIVERED" ? "success" : s.status === "IN_TRANSIT" ? "info" : "neutral"}>{s.status}</Badge>
            {s.carrier_name && <Badge>Vettore: {s.carrier_name}</Badge>}
            {s.actual_pallets != null && <Badge>Pallet: {s.actual_pallets}</Badge>}
            {s.delivery_window && <Badge>Fascia: {s.delivery_window}</Badge>}
            {s.has_ddt ? <Badge tone="success">DDT presente</Badge> : <Badge tone="warning">DDT mancante</Badge>}
          </div>
          <div style={{ marginTop: 6, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <Label>Data stimata di consegna</Label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span data-testid={`eta-value-${s.number}`}>
                  {s.expected_delivery_date ? fmtDateOnly(s.expected_delivery_date) : "Data di consegna in aggiornamento"}
                </span>
                {canEditEta && (
                  <Button size="sm" loading={busyId === s.id} disabled={busyId === s.id}
                          onClick={() => recalcEta(s.id)}
                          data-testid={`eta-recalc-${s.number}`}>Ricalcola</Button>
                )}
              </div>
            </div>
            {s.loading_date && <div><Label>Data carico</Label><div>{fmtDateOnly(s.loading_date)}</div></div>}
            {s.picked_up_at && <div><Label>Partenza</Label><div>{fmtDateTime(s.picked_up_at)}</div></div>}
          </div>
          {/* Feb 2026 — Data stimata consegna (informativa, read-only).
              Non sovrascrive expected/confirmed/requested_delivery_date. */}
          <DeliveryEstimateInline shipmentId={s.id} shipmentNumber={s.number} />
        </Card.Body></Card>
      ))}
    </Section>
  );
}

function AssignToLoadPlanCard({ orderId, shipmentId, deliveryPoint, estimatedPallets, onAssigned,
                                changeMode = false, currentPlan = null }) {
  const { apiGet, apiPost, token } = useAuth();
  const [carriers, setCarriers] = useState([]);
  const [compareData, setCompareData] = useState(null);
  // Mar 2026 — SENDER support. Default CARRIER; if changeMode inherits from currentPlan.
  const initialDeliveryMethod = (changeMode && currentPlan?.delivery_method === "SENDER")
    ? "SENDER" : "CARRIER";
  const [deliveryMethod, setDeliveryMethod] = useState(initialDeliveryMethod);
  const [selCarrier, setSelCarrier] = useState(changeMode && currentPlan?.carrier_id ? currentPlan.carrier_id : "");
  const [loadingDate, setLoadingDate] = useState(changeMode && currentPlan?.loading_date ? currentPlan.loading_date : "");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null); // { existing_plan|null }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Mar 2026 — Pre-selection Delivery Estimate map (carrier_id → item).
  // Popolata solo quando loadingDate è valorizzata; se assente le card mostrano
  // "Data stimata: inserisci data carico" senza chiamare il resolver.
  const [estimateMap, setEstimateMap] = useState({}); // { [carrier_id]: {estimated_delivery_date, unavailable_reason} }
  const [estimateLoading, setEstimateLoading] = useState(false);

  const pallets = Math.max(1, Math.ceil(Number(estimatedPallets || 1)));

  useEffect(() => {
    (async () => {
      try {
        setCarriers(await apiGet("/api/carriers").catch(() => []));
      } catch { /* ignore */ }
      if (deliveryPoint?.city || deliveryPoint?.province || deliveryPoint?.region) {
        try {
          const backend = process.env.REACT_APP_BACKEND_URL || "";
          const res = await fetch(`${backend}/api/logistics/carrier-tariffs/compare`, {
            method: "POST",
            headers: { "Content-Type": "application/json",
                       Authorization: `Bearer ${token || localStorage.getItem("access_token") || ""}` },
            body: JSON.stringify({
              city: deliveryPoint.city, province: deliveryPoint.province,
              region: deliveryPoint.region, pallets, kg: 0,
            }),
          });
          if (res.ok) setCompareData(await res.json());
        } catch { /* non-fatal */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Preview: when (carrier|SENDER) + date are set, check if a mutable LP already exists.
  useEffect(() => {
    const senderMode = deliveryMethod === "SENDER";
    if (!loadingDate || (!senderMode && !selCarrier)) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const carrierParam = senderMode ? "" : `&carrier_id=${selCarrier}`;
        const dmParam = `&delivery_method=${senderMode ? "SENDER" : "CARRIER"}`;
        const [d1, d2] = await Promise.all([
          apiGet(`/api/logistics/load-plans?status=DRAFT${carrierParam}${dmParam}&loading_date_from=${loadingDate}&loading_date_to=${loadingDate}`).catch(() => []),
          apiGet(`/api/logistics/load-plans?status=SENT_TO_CARRIER${carrierParam}${dmParam}&loading_date_from=${loadingDate}&loading_date_to=${loadingDate}`).catch(() => []),
        ]);
        if (cancelled) return;
        const existing = [...(d1 || []), ...(d2 || [])][0] || null;
        setPreview({ existing });
      } catch { if (!cancelled) setPreview({ existing: null }); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selCarrier, loadingDate, deliveryMethod]);

  // Mar 2026 — Batch Delivery Estimate preview.
  // Trigger: loadingDate valorizzata AND compareData disponibile AND (destinazione presente).
  // Reset a {} quando loadingDate è vuota o quando compareData cambia.
  useEffect(() => {
    setEstimateMap({});
    if (!loadingDate) return;
    const carrierIds = (compareData?.results || [])
      .filter(r => r.tariff !== null)
      .map(r => r.carrier_id);
    if (carrierIds.length === 0) return;
    if (!deliveryPoint?.city && !deliveryPoint?.province &&
        !deliveryPoint?.region && !deliveryPoint?.zip_code) return;
    let cancelled = false;
    setEstimateLoading(true);
    (async () => {
      try {
        const resp = await apiPost("/api/logistics/delivery-estimates/preview", {
          loading_date: loadingDate,
          carrier_ids: carrierIds,
          zip_code: deliveryPoint.zip_code || null,
          city: deliveryPoint.city || null,
          province: deliveryPoint.province || null,
          region: deliveryPoint.region || null,
        });
        if (cancelled) return;
        const map = {};
        for (const item of (resp.results || [])) {
          map[item.carrier_id] = {
            estimated_delivery_date: item.estimated_delivery_date || null,
            unavailable_reason: item.unavailable_reason || null,
          };
        }
        setEstimateMap(map);
      } catch {
        if (!cancelled) setEstimateMap({}); // non-blocking: card fallback
      } finally {
        if (!cancelled) setEstimateLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingDate, compareData, deliveryPoint?.zip_code, deliveryPoint?.city, deliveryPoint?.province, deliveryPoint?.region]);

  // Mar 2026 — Nessun limite arbitrario "top-3": mostriamo TUTTI i vettori
  // attivi ed economicamente eleggibili (con tariff valorizzato).
  // L'ordinamento (cost asc + tie-break carrier_id) è già applicato dal
  // backend compare_carriers.
  const top3 = (compareData?.results || []).filter(r => r.tariff !== null);
  // Mar 2026 — SENDER: solo Data carico obbligatoria; CARRIER: vettore + data.
  const isSender = deliveryMethod === "SENDER";
  const canSubmit = isSender ? !!loadingDate : (selCarrier && loadingDate);

  // Mar 2026 — "PIÙ VELOCE" ricalcolato dalle date stimate reali:
  //  - solo quando loadingDate è valorizzata E almeno una card ha una stima.
  //  - vincitore = min(estimated_delivery_date) tra le card top3 con stima.
  //  - in caso di pari data → NESSUN vincitore (comportamento neutro,
  //    come richiesto dallo spec: "non inventare un vincitore arbitrario").
  //  - se nessuna stima → nessun badge "PIÙ VELOCE" (soppresso rispetto
  //    al backend, che lo derivava da delivery_days).
  const fastestCarrierId = (() => {
    if (!loadingDate) return null;
    const withDate = top3
      .map(r => ({ id: r.carrier_id, d: estimateMap[r.carrier_id]?.estimated_delivery_date }))
      .filter(x => !!x.d);
    if (withDate.length === 0) return null;
    withDate.sort((a, b) => a.d.localeCompare(b.d));
    if (withDate.length >= 2 && withDate[0].d === withDate[1].d) return null; // tie → nessun vincitore
    return withDate[0].id;
  })();

  const assign = async () => {
    if (!canSubmit) {
      setErr(isSender ? "Data carico obbligatoria"
                      : "Vettore e Data carico obbligatori");
      return;
    }
    setBusy(true); setErr(null);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${backend}/api/logistics/load-plans/orders/${orderId}/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json",
                   Authorization: `Bearer ${token || localStorage.getItem("access_token") || ""}` },
        body: JSON.stringify({
          delivery_method: isSender ? "SENDER" : "CARRIER",
          carrier_id: isSender ? null : selCarrier,
          loading_date: loadingDate,
          notes: notes || null, force_change: changeMode ? true : false,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const d = data?.detail;
        const code = typeof d === "string" ? d : (d?.code || `HTTP ${res.status}`);
        throw new Error(code);
      }
      if (onAssigned) await onAssigned();
    } catch (e) {
      setErr(e?.message || "Assegnazione fallita");
    } finally { setBusy(false); }
  };

  const carrierName = (id) => carriers.find(c => c.id === id)?.business_name
                            || Object.values(compareData?.results || []).find(r => r.carrier_id === id)?.carrier?.business_name
                            || "—";

  return (
    <Card data-testid={changeMode ? "change-load-plan-card" : "assign-load-plan-card"} style={{ marginTop: 12 }}>
      <Card.Header><strong>{changeMode ? "Cambia Piano Carichi" : "Assegna a Piano Carichi"}</strong></Card.Header>
      <Card.Body>
        {changeMode && currentPlan && (
          <Alert tone="info" style={{ marginBottom: 12 }} data-testid="change-lp-current">
            Ordine attualmente su piano <strong>{currentPlan.number || currentPlan.id?.slice(0,8)}</strong>
            {' — '}{currentPlan.carrier_name || "vettore"} del <strong>{currentPlan.loading_date || "—"}</strong>
            {currentPlan.status ? ` (stato: ${currentPlan.status === "DRAFT" ? "Bozza" : currentPlan.status})` : null}
          </Alert>
        )}
        {err && <Alert tone="danger" data-testid="assign-lp-error">{err}</Alert>}

        {/* Mar 2026 — Delivery method switch: CARRIER (vettore esterno) vs
            SENDER (consegna a cura del mittente, GESUR GELATI). SENDER non
            richiede carrier_id, non manda email al vettore, non applica costo. */}
        <div style={{ marginBottom: 12 }} data-testid="assign-lp-delivery-method">
          <Label>Modalità consegna</Label>
          <div style={{ display: "inline-flex", border: "1px solid #DBE3EE",
                        borderRadius: 8, overflow: "hidden" }}>
            <button type="button"
                    data-testid="assign-lp-mode-carrier"
                    onClick={() => setDeliveryMethod("CARRIER")}
                    style={{ padding: "6px 14px", border: "none", cursor: "pointer",
                             background: !isSender ? "#0F4C81" : "#fff",
                             color: !isSender ? "#fff" : "#0F4C81",
                             fontWeight: 600 }}>
              Vettore
            </button>
            <button type="button"
                    data-testid="assign-lp-mode-sender"
                    onClick={() => { setDeliveryMethod("SENDER"); setSelCarrier(""); }}
                    style={{ padding: "6px 14px", border: "none", cursor: "pointer",
                             background: isSender ? "#0F4C81" : "#fff",
                             color: isSender ? "#fff" : "#0F4C81",
                             fontWeight: 600 }}>
              A cura del mittente
            </button>
          </div>
          {isSender && (
            <div data-testid="assign-lp-sender-badge"
                 style={{ marginTop: 8, padding: "8px 12px",
                          background: "#F3F6FA", border: "1px solid #DBE3EE",
                          borderRadius: 8, fontWeight: 600, color: "#0F4C81" }}>
              Consegna a cura del mittente — GESUR GELATI
            </div>
          )}
        </div>

        {!isSender && compareData !== null && (
          <div style={{ marginBottom: 16 }}>
            <Label>Vettori consigliati per {deliveryPoint?.city || "—"} ({pallets} pallet stimati)</Label>
            {top3.length === 0 ? (
              <p className="muted" style={{ fontSize: ".85rem" }} data-testid="no-suggested-carriers">
                Nessun vettore configurato per questa destinazione.
              </p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px,1fr))", gap: 8, marginTop: 6 }}>
                {top3.map(r => {
                  // Mar 2026 — Badge locali basati su dati reali:
                  //  - "PIÙ ECONOMICO" resta dal backend (`r.suggestion`).
                  //  - "PIÙ VELOCE" derivato SOLO dalle estimated_delivery_date reali.
                  const backendSugg = r.suggestion;
                  const isFastest = fastestCarrierId === r.carrier_id;
                  const showBackendSugg = backendSugg && backendSugg !== "PIÙ VELOCE";
                  const est = estimateMap[r.carrier_id];
                  let estText, estTestidSuffix;
                  if (!loadingDate) {
                    estText = "Data stimata: inserisci data carico";
                    estTestidSuffix = "pending-loading-date";
                  } else if (estimateLoading && !est) {
                    estText = "Calcolo data stimata…";
                    estTestidSuffix = "loading";
                  } else if (est?.estimated_delivery_date) {
                    estText = `Consegna stimata: ${new Date(est.estimated_delivery_date).toLocaleDateString("it-IT")}`;
                    estTestidSuffix = "value";
                  } else {
                    estText = "Data stimata non disponibile";
                    estTestidSuffix = "unavailable";
                  }
                  return (
                  <div key={r.carrier_id}
                       data-testid={`suggested-carrier-${r.carrier?.code || r.carrier_id}`}
                       onClick={() => setSelCarrier(r.carrier_id)}
                       style={{
                         border: selCarrier === r.carrier_id ? "2px solid #059669" : "1px solid #e5e7eb",
                         borderRadius: 8, padding: 10, cursor: "pointer",
                         background: selCarrier === r.carrier_id ? "#f0fdf4" : "#fff",
                       }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 6, flexWrap: "wrap" }}>
                      <strong>{r.carrier?.business_name || "—"}</strong>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {showBackendSugg && <Badge tone="info">{backendSugg}</Badge>}
                        {isFastest && (
                          <Badge tone="info"
                                 data-testid={`suggested-carrier-fastest-${r.carrier?.code || r.carrier_id}`}>
                            PIÙ VELOCE
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: ".85rem", color: "#475569", marginTop: 4 }}>
                      € {Number(r.tariff.total_cost).toFixed(2)}
                    </div>
                    <div style={{ fontSize: ".8rem", color: "#334155", marginTop: 2 }}
                         data-testid={`suggested-carrier-estimate-${estTestidSuffix}-${r.carrier?.code || r.carrier_id}`}>
                      {estText}
                    </div>
                    {/* Mar 2026 — Indicatore Giorno abituale per-card.
                        Fonte: carrier.loading_schedule_config (admin-managed).
                        Visibile subito appena esiste una Data carico, senza
                        dover prima selezionare il vettore nella select. */}
                    {loadingDate && (() => {
                      const fullCarrier = carriers.find(c => c.id === r.carrier_id);
                      const cfg = fullCarrier?.loading_schedule_config;
                      const hasCfg = cfg && typeof cfg === "object" &&
                                     Object.values(cfg).some(v => v && v.enabled);
                      if (!hasCfg) {
                        return (
                          <div className="muted" style={{ fontSize: ".75rem", marginTop: 2 }}
                               data-testid={`suggested-carrier-habit-unconfigured-${r.carrier?.code || r.carrier_id}`}>
                            Giorni abituali non configurati
                          </div>
                        );
                      }
                      const st = carrierPreferredStatus(fullCarrier, loadingDate);
                      if (st === "match") return (
                        <div style={{ fontSize: ".75rem", color: "#16a34a", marginTop: 2 }}
                             data-testid={`suggested-carrier-habit-match-${r.carrier?.code || r.carrier_id}`}>
                          ✓ Giorno abituale di carico
                        </div>
                      );
                      if (st === "mismatch") return (
                        <div style={{ fontSize: ".75rem", color: "#d97706", marginTop: 2 }}
                             data-testid={`suggested-carrier-habit-mismatch-${r.carrier?.code || r.carrier_id}`}>
                          ⚠ Data non abituale per questo vettore
                        </div>
                      );
                      return null;
                    })()}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div style={{ display: "grid", gap: 12,
                      gridTemplateColumns: isSender ? "1fr 1fr" : "1fr 1fr 1fr",
                      alignItems: "end" }}>
          {!isSender && (
          <div>
            <Label>Vettore *</Label>
            <select className="ds-input" value={selCarrier}
                    data-testid="assign-lp-carrier-select"
                    onChange={e => setSelCarrier(e.target.value)}>
              <option value="">— seleziona vettore —</option>
              {sortCarriersByPreferred(carriers, loadingDate).map(c => {
                const st = carrierPreferredStatus(c, loadingDate);
                const marker = st === "match" ? " ✓ abituale" : "";
                return <option key={c.id} value={c.id}>{c.business_name}{marker}</option>;
              })}
            </select>
            {(() => {
              const sel = carriers.find(c => c.id === selCarrier);
              const st = carrierPreferredStatus(sel, loadingDate);
              // Mar 2026 — Indicatore giorno abituale, informativo (non bloccante).
              // Fonte: carrier.loading_schedule_config (admin-managed). Se nessun
              // vettore è ancora selezionato → nessun verdetto definitivo.
              if (!sel || !loadingDate) return null;
              if (st === "match") return <div data-testid="assign-lp-hint-match" style={{ fontSize: ".78rem", color: "#16a34a", marginTop: 4 }}>Giorno abituale di carico</div>;
              if (st === "mismatch") return <div data-testid="assign-lp-hint-mismatch" style={{ fontSize: ".78rem", color: "#d97706", marginTop: 4 }}>⚠ Data non abituale per questo vettore</div>;
              return null;
            })()}
          </div>
          )}
          <div>
            <Label>Data carico *</Label>
            <Input type="date" value={loadingDate}
                   data-testid="assign-lp-loading-date"
                   onChange={e => setLoadingDate(e.target.value)} />
          </div>
          <div>
            <Label>Note (opzionali)</Label>
            <Input value={notes} data-testid="assign-lp-notes"
                   onChange={e => setNotes(e.target.value)} placeholder="—" />
          </div>
        </div>

        {canSubmit && preview !== null && (
          <Alert tone={preview.existing ? "info" : "success"}
                 data-testid="assign-lp-preview" style={{ marginTop: 12 }}>
            {preview.existing
              ? <>Verrà aggiunto al Piano Carichi esistente <strong>LP-{preview.existing.loading_date}-{String(preview.existing.id).slice(0,8)}</strong> ({isSender ? "Consegna a cura del mittente" : carrierName(preview.existing.carrier_id)}, {preview.existing.status === "DRAFT" ? "Bozza" : "Inviato al vettore"}).</>
              : <>Verrà creato un nuovo Piano Carichi in bozza per <strong>{isSender ? "Consegna a cura del mittente — GESUR GELATI" : carrierName(selCarrier)}</strong> del <strong>{loadingDate}</strong>.</>}
          </Alert>
        )}

        <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="primary" onClick={assign} loading={busy} disabled={busy || !canSubmit}
                  data-testid={changeMode ? "change-lp-submit" : "assign-lp-submit"}>
            {busy ? (changeMode ? "Cambio…" : "Assegno…")
                  : (changeMode ? "Conferma cambio piano"
                                : (preview?.existing ? "Aggiungi al Piano" : "Crea e assegna"))}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}

function ResidualLineageCard({ prep }) {
  if (!prep || (!prep.residual_parent && (!prep.residual_children || prep.residual_children.length === 0))) return null;
  return (
    <Card data-testid="residual-lineage-card" style={{ marginTop: 12 }}>
      <Card.Body>
        {prep.residual_parent && (
          <div data-testid="residual-of-parent">
            <strong>Residuo dell'ordine:</strong>{" "}
            <a href={`/logistics/ship/${prep.residual_parent.id}`}
               data-testid={`residual-parent-link-${prep.residual_parent.number}`}>
              {prep.residual_parent.number}
            </a>{" "}
            <Badge tone="info">{prep.residual_parent.status}</Badge>
          </div>
        )}
        {(prep.residual_children || []).map(c => (
          <div key={c.id} data-testid={`residual-child-${c.number}`}>
            <strong>Residuo generato:</strong>{" "}
            <a href={`/logistics/ship/${c.id}`}
               data-testid={`residual-child-link-${c.number}`}>{c.number}</a>{" "}
            <Badge tone="warning">{c.status}</Badge>
          </div>
        ))}
      </Card.Body>
    </Card>
  );
}


function ConsuntivaWizard({
  prep, selCarrier, setSelCarrier, expDate, setExpDate, window, setWindow,
  pallets, setPallets, palletsErr, setPalletsErr, notes, setNotes,
  linesToShip, setLinesToShip, ddtFile, setDdtFile, ddtNumber, setDdtNumber,
  ddtDate, setDdtDate, carriers, onBack, onSubmitted, setErr, token, orderId,
}) {
  const { apiPost, apiGet } = useAuth();
  // Flow v8 — 3 esiti: FULL | CLOSE_RESIDUAL | KEEP_RESIDUAL
  const [outcome, setOutcome] = useState("FULL");
  const [closeReason, setCloseReason] = useState("CUSTOMER_REQUEST");
  const [closeNote, setCloseNote] = useState("");
  const [busy, setBusy] = useState(false);
  // DDT auto-lookup (Zucchetti import folder) — opt-in, feature-flagged
  // by backend ENV DDT_IMPORT_FOLDER. Endpoint returns 503 when unset:
  // in that case we simply hide the "Cerca DDT" button.
  const [folderDisabled, setFolderDisabled] = useState(false);
  const [folderSearch, setFolderSearch] = useState(null); // {status, normalized, matches}
  const [folderBusy, setFolderBusy] = useState(false);
  const [folderErr, setFolderErr] = useState(null);
  const [folderDdt, setFolderDdt] = useState(null); // {id, filename_original} once attached
  const [ddtKind, setDdtKind] = useState("STANDARD");

  // Compute residual & stralcio per line based on outcome + linesToShip
  const rows = (prep.lines || []).map(l => {
    const shipped = Number(linesToShip[l.order_line_id] || 0);
    const residual = Math.max(0, l.qty_residual - shipped);
    return { ...l, ship_now: shipped, residual_after: residual };
  });
  const hasResidual = rows.some(r => r.residual_after > 0);

  // Force-fill FULL: all rows get qty_residual.
  // BUG FIX: `prep` è caricato asincrono; il vecchio useEffect con deps [outcome]
  // non veniva ri-eseguito quando `prep` arrivava DOPO il mount con outcome default
  // "FULL", lasciando linesToShip = {} e il submit falliva con "Inserisci almeno
  // una quantità da spedire". Ora dipende anche da prep.
  useEffect(() => {
    if (outcome === "FULL") {
      const next = {};
      (prep.lines || []).forEach(l => { next[l.order_line_id] = l.qty_residual; });
      setLinesToShip(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outcome, prep]);

  // DDT enforcement (UI parity with backend missing_ddt guard).
  // Se la DRAFT già associata all'ordine ha un DDT caricato in precedenza,
  // il requisito è soddisfatto e non chiediamo un nuovo upload.
  const _draftShip = (prep.existing_shipments || []).find(s => s.status === "DRAFT");
  const existingDdt = _draftShip?.has_ddt ? { number: _draftShip?.number } : null;

  const submit = async () => {
    setBusy(true); setErr(null);
    const v = validatePallets(pallets);
    if (!v.ok) { setPalletsErr(v.err); setErr(v.err); setBusy(false); return; }
    setPalletsErr(null);
    try {
      // DDT MANDATORIO prima della partenza. Regola v2 (2026-02): il DDT
      // è requisito per la transizione DRAFT → IN_TRANSIT (allineamento UI
      // con il controllo backend missing_ddt che scatta alla conferma LP).
      // folderDdt: se il DDT è già stato allegato dalla cartella Zucchetti
      // la DeliveryNote è persistita → il file manuale non è più richiesto.
      if (!existingDdt && !folderDdt && (!ddtFile || !ddtNumber.trim() || !ddtDate)) {
        throw new Error("Carica il DDT prima di confermare la partenza");
      }
      // FULL: deriva SEMPRE le righe direttamente da prep.lines per evitare race
      // con lo stato React. Per gli altri outcome usa le quantità inserite manualmente.
      const lines = outcome === "FULL"
        ? (prep.lines || [])
            .filter(l => Number(l.qty_residual) > 0)
            .map(l => ({ order_line_id: l.order_line_id, qty_cartons: Number(l.qty_residual) }))
        : Object.entries(linesToShip)
            .filter(([_, q]) => Number(q) > 0)
            .map(([order_line_id, qty]) => ({ order_line_id, qty_cartons: Number(qty) }));
      if (lines.length === 0) throw new Error("Inserisci almeno una quantità da spedire");
      // Validate qty <= ordered per line
      for (const r of rows) {
        if (r.ship_now > r.qty_residual) {
          throw new Error(`Riga ${r.sku}: qty spedita (${r.ship_now}) > residuo (${r.qty_residual})`);
        }
      }
      // Case CLOSE_RESIDUAL needs a reason
      if (outcome === "CLOSE_RESIDUAL" && closeReason === "OTHER" && !closeNote.trim()) {
        throw new Error("Motivo dello stralcio obbligatorio quando 'Altro' è selezionato");
      }

      // 1. Create/promote shipment as DRAFT (canonical path)
      const shp = await apiPost("/api/shipments", {
        order_id: orderId,
        carrier_id: selCarrier || null,
        expected_delivery_date: expDate || null,
        delivery_window: window || null,
        actual_pallets: v.value,
        notes: notes || null,
        lines,
        status: "DRAFT",
      });

      // 2. DDT upload if provided (skip: già persistito via folder attach)
      if (!folderDdt && ddtFile && ddtNumber && ddtDate) {
        const fd = new FormData();
        fd.append("ddt_number", ddtNumber);
        fd.append("ddt_date", ddtDate);
        fd.append("kind", ddtKind);
        fd.append("file", ddtFile);
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/shipments/${shp.id}/ddt`, {
          method: "POST", body: fd,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`DDT upload failed: ${res.status}`);
      }

      // 2b. Conferma partenza — canonico (Shipment IN_TRANSIT ⇔ mark_departed)
      await apiPost(`/api/shipments/${shp.id}/mark-departed`, {});

      // 3. Outcome-specific follow-up
      let residualOrder = null;
      if (outcome === "CLOSE_RESIDUAL" && hasResidual) {
        for (const r of rows) {
          if (r.residual_after > 0) {
            const body = {
              closed_qty_cartons: r.residual_after,
              close_reason: closeReason,
            };
            if (closeReason === "OTHER") body.close_reason_note = closeNote;
            else if (closeNote) body.internal_note = closeNote;
            await apiPost(`/api/order-lines/${r.order_line_id}/close-residual`, body);
          }
        }
      } else if (outcome === "KEEP_RESIDUAL" && hasResidual) {
        residualOrder = await apiPost(`/api/shipments/${shp.id}/create-residual-order`, { idempotent: true });
      }

      onSubmitted({ ...shp, _residualOrder: residualOrder, _outcome: outcome });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || String(e));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Section title="Consuntiva carico / Conferma partenza"
               actions={<Button size="sm" data-testid="btn-back-planning" onClick={onBack}>← Torna a pianificazione</Button>}>
        <Alert tone="warning">
          Consuntivazione: le quantità inserite creeranno lo shipment, faranno partire l'ordine
          e (a seconda dell'esito scelto) chiuderanno o sposteranno il residuo.
        </Alert>
      </Section>

      <Section title="1 — Esito evasione">
        <Card data-testid="outcome-selector"><Card.Body>
          <div style={{ display: "grid", gap: 10 }}>
            {[
              { v: "FULL", t: "Evaso completamente", d: "Tutte le quantità ordinate vengono spedite. Alla consegna l'ordine sarà DELIVERED." },
              { v: "CLOSE_RESIDUAL", t: "Evaso parzialmente — chiudi residuo (stralcio)", d: "Il residuo non spedito viene chiuso definitivamente. Alla consegna l'ordine sarà DELIVERED." },
              { v: "KEEP_RESIDUAL", t: "Evaso parzialmente — crea ordine residuo", d: "Il residuo confluisce in un nuovo ordine PROCESSING collegato al parent. Alla consegna l'ordine originale sarà DELIVERED." },
            ].map(o => (
              <label key={o.v} data-testid={`outcome-${o.v}`}
                     style={{ display: "flex", gap: 10, alignItems: "flex-start",
                              border: outcome === o.v ? "2px solid #059669" : "1px solid #e5e7eb",
                              borderRadius: 8, padding: 10, cursor: "pointer",
                              background: outcome === o.v ? "#f0fdf4" : "#fff" }}>
                <input type="radio" name="outcome" value={o.v} checked={outcome === o.v}
                       onChange={() => setOutcome(o.v)} />
                <div>
                  <strong>{o.t}</strong>
                  <div className="muted" style={{ fontSize: ".85rem" }}>{o.d}</div>
                </div>
              </label>
            ))}
          </div>
        </Card.Body></Card>
      </Section>

      <Section title="2 — Quantità da spedire">
        <Table
          columns={[
            { key: "sku", label: "SKU" },
            { key: "name", label: "Prodotto" },
            { key: "qty_ordered", label: "Ord." },
            { key: "qty_already_shipped", label: "Già spedito" },
            { key: "qty_residual", label: "Residuo" },
            { key: "qty_ship", label: "Da spedire ora", render: (r) => (
              <Input type="number" min="0" max={r.qty_residual}
                     data-testid={`ship-${r.sku}`}
                     disabled={outcome === "FULL"}
                     value={linesToShip[r.order_line_id] ?? ""}
                     onChange={(e) => setLinesToShip({ ...linesToShip, [r.order_line_id]: e.target.value })} />
            )},
            { key: "residual_impact", label: outcome === "CLOSE_RESIDUAL" ? "Stralciato" : "Residuo nuovo ordine",
              render: (r) => {
                const shipped = Number(linesToShip[r.order_line_id] || 0);
                const residual = Math.max(0, r.qty_residual - shipped);
                if (outcome === "FULL") return "—";
                if (residual === 0) return <span className="muted">—</span>;
                return <span data-testid={`residual-preview-${r.sku}`}
                             style={{ color: outcome === "CLOSE_RESIDUAL" ? "#b91c1c" : "#0369a1" }}>
                  {residual}
                </span>;
              }
            },
          ]}
          rows={prep.lines}
        />
      </Section>

      {outcome === "CLOSE_RESIDUAL" && hasResidual && (
        <Section title="2b — Motivo dello stralcio">
          <Card><Card.Body>
            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 2fr" }}>
              <div>
                <Label>Motivo</Label>
                <select className="ds-input" value={closeReason}
                        data-testid="close-reason-select"
                        onChange={e => setCloseReason(e.target.value)}>
                  <option value="CUSTOMER_REQUEST">Richiesta cliente</option>
                  <option value="SUBSTITUTION_REFUSED">Sostituzione non accettata</option>
                  <option value="NOT_NEEDED">Articolo non più necessario</option>
                  <option value="OTHER">Altro (nota obbligatoria)</option>
                </select>
              </div>
              <div>
                <Label>{closeReason === "OTHER" ? "Motivo (obbligatorio)" : "Nota interna (opzionale)"}</Label>
                <Input value={closeNote} data-testid="close-reason-note"
                       onChange={e => setCloseNote(e.target.value)} />
              </div>
            </div>
          </Card.Body></Card>
        </Section>
      )}

      <Section title="3 — Vettore & date">
        <Card><Card.Body>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1rem" }}>
            <div>
              <Label>Vettore</Label>
              <select className="ds-input" data-testid="carrier-select" value={selCarrier} onChange={(e) => setSelCarrier(e.target.value)}>
                <option value="">—</option>
                {sortCarriersByPreferred(carriers, expDate).map((c) => {
                  const st = carrierPreferredStatus(c, expDate);
                  const marker = st === "match" ? " ✓ abituale" : "";
                  return <option key={c.id} value={c.id}>{c.business_name}{marker}</option>;
                })}
              </select>
              {(() => {
                const sel = carriers.find(c => c.id === selCarrier);
                const st = carrierPreferredStatus(sel, expDate);
                if (st === "match") return <div data-testid="ship-carrier-hint-match" style={{ fontSize: ".78rem", color: "#16a34a", marginTop: 4 }}>Giorno abituale per questo vettore</div>;
                if (st === "mismatch") return <div data-testid="ship-carrier-hint-mismatch" style={{ fontSize: ".78rem", color: "#d97706", marginTop: 4 }}>⚠ Giorno non abituale per questo vettore</div>;
                return null;
              })()}
            </div>
            <div><Label>Data prevista</Label><Input type="date" data-testid="expected-date" value={expDate} onChange={(e) => setExpDate(e.target.value)} />
              <div className="muted" style={{ fontSize: ".8rem" }} data-testid="expected-date-canonical-note">La data stimata comunicata al cliente è calcolata dalle regole del vettore.</div></div>
            <div><Label>Fascia oraria</Label><Input placeholder="es. 09:00-13:00" value={window} onChange={(e) => setWindow(e.target.value)} /></div>
            <div>
              <Label>Pallet effettivi</Label>
              <Input type="number" min="1" step="1" inputMode="numeric"
                     data-testid="pallets-effective" value={pallets}
                     onChange={(e) => {
                       const v = e.target.value; setPallets(v);
                       const r = validatePallets(v);
                       setPalletsErr(v === "" ? null : (r.ok ? null : r.err));
                     }} />
              {palletsErr && <div className="muted" style={{ color: "#b91c1c", fontSize: 12, marginTop: 2 }}
                                  data-testid="pallets-error">{palletsErr}</div>}
            </div>
            <div style={{ gridColumn: "1 / -1" }}><Label>Note</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          </div>
        </Card.Body></Card>
      </Section>

      <Section title="4 — DDT (PDF, obbligatorio) *">
        <Card><Card.Body>
          {existingDdt ? (
            <Alert tone="success" data-testid="ddt-existing-info">
              <strong>DDT già presente</strong> sulla spedizione {existingDdt.number}.
              Il requisito è soddisfatto: puoi confermare la partenza senza ricaricare il PDF.
              Se serve sostituirlo, dopo la partenza usa "Carica / Sostituisci DDT" dal dettaglio della spedizione.
            </Alert>
          ) : folderDdt ? (
            <Alert tone="success" data-testid="ddt-attached-from-folder">
              <strong>DDT allegato dalla cartella:</strong> {folderDdt.filename_original}.
              Il requisito è soddisfatto. Puoi confermare la partenza.
            </Alert>
          ) : (
            <>
              <Alert tone="info" style={{ marginBottom: 10 }} data-testid="ddt-required-hint">
                Il DDT è richiesto prima della partenza della merce. Senza DDT la spedizione non può passare a IN_TRANSIT.
              </Alert>
              {/* Auto-lookup dalla cartella Zucchetti — feature opt-in via
                  backend DDT_IMPORT_FOLDER. Se l'endpoint risponde 503,
                  nascondiamo il pulsante e resta il solo upload manuale. */}
              {_draftShip && !folderDisabled && (
                <div style={{ marginBottom: 10, padding: 10,
                              background: "#f8fafc", borderRadius: 6,
                              border: "1px solid #e2e8f0" }}
                     data-testid="ddt-folder-lookup">
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 220px" }}>
                      <Label>Cerca DDT dalla cartella Zucchetti</Label>
                      <div style={{ display: "flex", gap: 6 }}>
                        <Input placeholder="es. 2382/2026"
                               data-testid="ddt-folder-input"
                               value={ddtNumber}
                               onChange={(e) => { setDdtNumber(e.target.value); setFolderSearch(null); setFolderErr(null); }} />
                        <Button size="sm" variant="primary" disabled={folderBusy || !ddtNumber.trim()}
                                data-testid="ddt-folder-search-btn"
                                onClick={async () => {
                                  setFolderBusy(true); setFolderErr(null); setFolderSearch(null);
                                  try {
                                    const q = encodeURIComponent(ddtNumber.trim());
                                    const res = await apiGet(`/api/shipments/${_draftShip.id}/ddt/search?ddt_number=${q}`);
                                    setFolderSearch(res);
                                  } catch (e) {
                                    if (e?.status === 503) { setFolderDisabled(true); }
                                    else { setFolderErr(humanErr(e?.data?.detail || e?.message || String(e))); }
                                  } finally { setFolderBusy(false); }
                                }}>
                          {folderBusy ? "Cerco…" : "Cerca DDT"}
                        </Button>
                      </div>
                    </div>
                  </div>
                  {folderErr && <Alert tone="danger" data-testid="ddt-folder-err" style={{ marginTop: 8 }}>{folderErr}</Alert>}
                  {folderSearch?.status === "not_found" && (
                    <Alert tone="info" data-testid="ddt-folder-not-found" style={{ marginTop: 8 }}>
                      Nessun file trovato nella cartella per <strong>{folderSearch.normalized}</strong>. Usa l'upload manuale qui sotto.
                    </Alert>
                  )}
                  {folderSearch?.status === "found" && folderSearch.matches?.[0] && (
                    <Alert tone="success" data-testid="ddt-folder-found" style={{ marginTop: 8 }}>
                      DDT trovato: <strong>{folderSearch.matches[0].filename}</strong>{" "}
                      ({Math.round(folderSearch.matches[0].size_bytes / 1024)} KB)
                      <div style={{ marginTop: 8 }}>
                        <Button size="sm" variant="primary" disabled={folderBusy}
                                data-testid="ddt-folder-attach-btn"
                                onClick={async () => {
                                  setFolderBusy(true); setFolderErr(null);
                                  try {
                                    const res = await apiPost(`/api/shipments/${_draftShip.id}/ddt/attach-from-folder`, {
                                      filename: folderSearch.matches[0].filename,
                                      ddt_number: ddtNumber.trim() || null,
                                      ddt_date: ddtDate || null,
                                      kind: ddtKind,
                                    });
                                    setFolderDdt({ id: res.id, filename_original: res.filename_original });
                                  } catch (e) {
                                    setFolderErr(humanErr(e?.data?.detail || e?.message || String(e)));
                                  } finally { setFolderBusy(false); }
                                }}>
                          Allega DDT
                        </Button>
                      </div>
                    </Alert>
                  )}
                  {folderSearch?.status === "conflict" && (
                    <Alert tone="warning" data-testid="ddt-folder-conflict" style={{ marginTop: 8 }}>
                      Trovati {folderSearch.matches.length} file compatibili — scegli manualmente quale allegare:
                      <ul style={{ margin: "6px 0 0 18px" }}>
                        {folderSearch.matches.map(m => (
                          <li key={m.filename} style={{ marginTop: 4 }}>
                            <code>{m.filename}</code>{" "}
                            <Button size="sm" variant="outline" disabled={folderBusy}
                                    data-testid={`ddt-folder-attach-${m.filename}`}
                                    onClick={async () => {
                                      setFolderBusy(true); setFolderErr(null);
                                      try {
                                        const res = await apiPost(`/api/shipments/${_draftShip.id}/ddt/attach-from-folder`, {
                                          filename: m.filename,
                                          ddt_number: ddtNumber.trim() || null,
                                          ddt_date: ddtDate || null,
                                          kind: ddtKind,
                                        });
                                        setFolderDdt({ id: res.id, filename_original: res.filename_original });
                                      } catch (e) {
                                        setFolderErr(humanErr(e?.data?.detail || e?.message || String(e)));
                                      } finally { setFolderBusy(false); }
                                    }}>
                              Allega
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1rem" }}
                   data-testid="ddt-manual-upload">
                <div style={{ gridColumn: "1 / -1", fontWeight: 600, marginBottom: -8 }}>
                  Modalità 2 — Carica DDT manualmente
                </div>
                <div><Label>Numero DDT *</Label><Input data-testid="ddt-number" required value={ddtNumber} onChange={(e) => setDdtNumber(e.target.value)} /></div>
                <div><Label>Data DDT *</Label><Input type="date" required data-testid="ddt-date" value={ddtDate} onChange={(e) => setDdtDate(e.target.value)} /></div>
                <div><Label>File PDF *</Label><input required data-testid="ddt-file" type="file" accept="application/pdf" onChange={(e) => setDdtFile(e.target.files?.[0] || null)} /></div>
                <div>
                  <Label>Tipo DDT</Label>
                  <select className="ds-input" value={ddtKind}
                          onChange={(e) => setDdtKind(e.target.value)}
                          data-testid="ddt-kind">
                    <option value="STANDARD">DDT standard</option>
                    <option value="SAMPLE">DDT campionatura</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </Card.Body></Card>
      </Section>

      <Section title="5 — Conferma">
        <Button data-testid="wizard-submit" variant="primary" size="lg"
                onClick={submit} loading={busy} disabled={busy}>
          {busy ? "Confermo…" : (
            outcome === "FULL" ? "Crea spedizione (completo)"
            : outcome === "CLOSE_RESIDUAL" ? "Crea spedizione + Chiudi residuo"
            : "Crea spedizione + Crea nuovo ordine residuo"
          )}
        </Button>
      </Section>
    </>
  );
}


export function LogisticsShipmentWizard() {
  const { orderId } = useParams();
  const { apiGet, apiPost, token } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // Feb 2026 — ritorno contestuale: se aperto da un tab della Home logistica,
  // il bottone "← Torna a X" riporta al tab esatto (con activeTab in state
  // così LogisticsHome lo ripristina). Se aperto da un Piano Carichi, mantieni
  // il bottone dedicato "← Torna al Piano Carichi" già presente. Se nessun
  // contesto: fallback "← Home logistica".
  const fromTab = location.state?.fromTab || null;
  const _tabBack = {
    ricevuti: { label: "Ordini ricevuti", testid: "back-to-ricevuti" },
    da_preparare: { label: "Ordini da preparare", testid: "back-to-da-preparare" },
    in_piano: { label: "In piano carichi", testid: "back-to-in-piano" },
    in_consegna: { label: "In consegna", testid: "back-to-in-consegna" },
    consegnati: { label: "Consegnati", testid: "back-to-consegnati" },
    ddt_mancanti: { label: "DDT mancanti", testid: "back-to-ddt" },
  };
  const backCtx = fromTab && _tabBack[fromTab] ? _tabBack[fromTab] : null;
  const [prep, setPrep] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [selCarrier, setSelCarrier] = useState("");
  const [expDate, setExpDate] = useState("");
  const [window, setWindow] = useState("");
  const [pallets, setPallets] = useState("");
  const [palletsErr, setPalletsErr] = useState(null);
  const [notes, setNotes] = useState("");
  const [linesToShip, setLinesToShip] = useState({});
  const [ddtFile, setDdtFile] = useState(null);
  const [ddtNumber, setDdtNumber] = useState("");
  const [ddtDate, setDdtDate] = useState("");
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [consuntivaMode, setConsuntivaMode] = useState(false);
  const [changeLpOpen, setChangeLpOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setPrep(await apiGet(`/api/logistics/order/${orderId}/prepare`));
        setCarriers(await apiGet("/api/carriers").catch(() => []));
      } catch (e) { setErr("Errore caricamento"); }
    })();
  }, [orderId, reloadTick]);

  const submit = async () => {
    setErr(null);
    const v = validatePallets(pallets);
    if (!v.ok) { setPalletsErr(v.err); setErr(v.err); return; }
    setPalletsErr(null);
    try {
      // DDT MANDATORIO prima della partenza. Se la DRAFT già associata all'ordine
      // ha un DDT precedente, il requisito è soddisfatto; altrimenti UI blocca
      // (allineamento con missing_ddt che il backend applicherebbe al confirm LP).
      const _draftShip = (prep?.existing_shipments || []).find(s => s.status === "DRAFT");
      const _existingDdt = _draftShip?.has_ddt;
      if (!_existingDdt && (!ddtFile || !ddtNumber.trim() || !ddtDate)) {
        throw new Error("Carica il DDT prima di confermare la partenza");
      }
      // Deriva le lines: se l'operatore ha inserito quantità manuali le rispetta,
      // altrimenti (submit "senza campi valorizzati" = intento FULL da UI) deriva
      // automaticamente qty_shipped = qty_residual per tutte le righe del prep.
      // Robustezza: nessun errore "Inserisci almeno una quantità da spedire" se ci
      // sono righe con residuo > 0 da spedire.
      let lines = Object.entries(linesToShip)
        .filter(([, q]) => Number(q) > 0)
        .map(([order_line_id, qty]) => ({ order_line_id, qty_cartons: Number(qty) }));
      if (lines.length === 0) {
        lines = (prep?.lines || [])
          .filter(l => Number(l.qty_residual) > 0)
          .map(l => ({ order_line_id: l.order_line_id, qty_cartons: Number(l.qty_residual) }));
      }
      if (lines.length === 0) throw new Error("Nessuna riga con residuo da spedire");
      const shp = await apiPost("/api/shipments", {
        order_id: orderId,
        carrier_id: selCarrier || null,
        expected_delivery_date: expDate || null,
        delivery_window: window || null,
        actual_pallets: v.value,
        notes: notes || null,
        lines,
        status: "DRAFT",
      });
      if (ddtFile && ddtNumber && ddtDate) {
        const fd = new FormData();
        fd.append("ddt_number", ddtNumber);
        fd.append("ddt_date", ddtDate);
        fd.append("file", ddtFile);
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/shipments/${shp.id}/ddt`, {
          method: "POST", body: fd,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`DDT upload failed: ${res.status}`);
      }
      // Conferma partenza — canonico
      await apiPost(`/api/shipments/${shp.id}/mark-departed`, {});
      setDone(shp);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  };

  if (!prep) return <Card><Card.Body className="muted">Caricamento…</Card.Body></Card>;
  if (done) return (
    <Section title="Spedizione creata ✓"
             actions={<Button onClick={() => nav("/logistics")}>← Home logistica</Button>}>
      <Alert tone="success" data-testid="wizard-done">
        Spedizione <strong>{done.number}</strong> creata (status: {done.status}).
      </Alert>
      {done._residualOrder && (
        <Alert tone="info" data-testid="residual-created-banner" style={{ marginTop: 10 }}>
          Nuovo ordine residuo creato:{" "}
          <strong data-testid="residual-created-number">{done._residualOrder.number}</strong>{" "}
          (status {done._residualOrder.status}).{" "}
          <a href={`/logistics/ship/${done._residualOrder.id}`}
             data-testid="residual-created-link">Apri ordine residuo →</a>
        </Alert>
      )}
      {done._outcome === "CLOSE_RESIDUAL" && (
        <Alert tone="info" data-testid="stralcio-done" style={{ marginTop: 10 }}>
          Residuo stralciato. L'ordine originale sarà chiuso in DELIVERED alla consegna.
        </Alert>
      )}
    </Section>
  );

  const st = prep.order.status;

  return (
    <>
      <Section title={`Ordine — ${prep.order.number}`}
               actions={
                 <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                   <StatusBadge status={st} />
                   {prep.load_plan?.id && (
                     <Button size="sm" variant="outline"
                             data-testid="back-to-load-plan"
                             onClick={() => nav(`/logistics/load-plans/${prep.load_plan.id}`)}>
                       ← Torna al Piano Carichi
                     </Button>
                   )}
                   {backCtx && !prep.load_plan?.id && (
                     <Button size="sm" variant="outline"
                             data-testid={backCtx.testid}
                             onClick={() => nav("/logistics", { state: { activeTab: fromTab } })}>
                       ← {backCtx.label}
                     </Button>
                   )}
                   <Button size="sm" onClick={() => nav("/logistics")} data-testid="back-to-logistics">← Home logistica</Button>
                 </div>
               }>
        {err && <Alert tone="danger" data-testid="wizard-error">{err}</Alert>}
      </Section>

      {st === "WAITING_PAYMENT" && <WaitingPaymentBanner prep={prep} />}

      <OrderContextSection prep={prep} onDone={() => setReloadTick((t) => t + 1)} />
      <ResidualLineageCard prep={prep} />

      {(st === "RECEIVED" || st === "WAITING_PAYMENT" || st === "PROCESSING" ||
        st === "SHIPPING" || st === "IN_DELIVERY" || st === "DELIVERED") && (
        <OrderLinesReadOnlySection lines={prep.lines} order={prep.order} />
      )}

      {st === "RECEIVED" && prep.order?.delivery_mode === "PICKUP" && (
        <PickupLocationChangeCard prep={prep}
                                  onDone={() => setReloadTick((t) => t + 1)} />
      )}

      {st === "RECEIVED" && (
        <Section title="Azioni operative">
          <Card data-testid="received-actions-card"><Card.Body>
            <ReceivedActionsInline entry={{ id: prep.order.id, number: prep.order.number }}
                                   onDone={() => setReloadTick((t) => t + 1)} />
          </Card.Body></Card>
        </Section>
      )}

      {st === "PROCESSING" && prep.order?.delivery_mode === "PICKUP" && (
        <PickupReadyCard prep={prep} token={token}
                         onDone={() => setReloadTick((t) => t + 1)} />
      )}

      {st === "READY_FOR_PICKUP" && (
        <Section title="Pronto per il ritiro">
          <Card data-testid="pickup-ready-view"><Card.Body>
            <Alert tone="success" data-testid="pickup-ready-status">
              L'ordine è pronto per il ritiro presso la nostra sede.
              Il cliente è stato notificato via email con il DDT allegato.
            </Alert>
          </Card.Body></Card>
        </Section>
      )}

      {st === "PROCESSING" && prep.order?.delivery_mode !== "PICKUP" && !consuntivaMode && (
        <>
          <Section title="Da preparare — pianificazione"
                   actions={
                     <Button variant="primary" size="sm"
                             data-testid="btn-open-consuntiva"
                             onClick={() => setConsuntivaMode(true)}>
                       Consuntiva carico / Conferma partenza →
                     </Button>
                   }>
            <Card data-testid="processing-planning-view"><Card.Body>
              <Table
                columns={[
                  { key: "sku", label: "SKU" },
                  { key: "name", label: "Prodotto" },
                  { key: "qty_ordered", label: "Ord." },
                  { key: "weight_kg_estimated", label: "kg stimati",
                    render: (r) => (r.weight_kg_estimated ?? r.total_weight_kg ?? "—") },
                  { key: "pallets_estimated", label: "Pallet stimati",
                    render: (r) => (r.pallets_estimated ?? "—") },
                ]}
                rows={prep.lines}
              />
              <div style={{ marginTop: 12, display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "1rem" }}
                   data-testid="processing-planning-meta">
                <div><Label>Load Plan</Label>
                     <div className="muted">{prep.load_plan?.number || "—"}</div></div>
                <div><Label>Vettore (da LP)</Label>
                     <div className="muted">{prep.load_plan?.carrier_name || prep.carrier?.business_name || "—"}</div></div>
                <div><Label>Data carico prevista</Label>
                     <div className="muted">{prep.load_plan?.loading_date || prep.order?.expected_delivery_date || "—"}</div></div>
                <div><Label>Note logistiche</Label>
                     <div className="muted">{prep.order?.logistics_notes || "—"}</div></div>
              </div>
              <Alert tone="info" style={{ marginTop: 12 }}>
                Vista solo pianificazione. Quantità spedite, pallet effettivi, chiusura residui e DDT
                sono disponibili nella fase «Consuntiva carico / Conferma partenza».
              </Alert>
              {(() => {
                const draftShip = (prep.existing_shipments || []).find(s => s.status === "DRAFT");
                if (!prep.load_plan && draftShip && prep.delivery_point) {
                  return (
                    <AssignToLoadPlanCard
                      orderId={prep.order.id}
                      shipmentId={draftShip.id}
                      deliveryPoint={prep.delivery_point}
                      estimatedPallets={prep.order?.estimated_pallets_total}
                      onAssigned={() => setReloadTick(t => t + 1)}
                    />
                  );
                }
                if (!prep.load_plan && draftShip && !prep.delivery_point) {
                  return (
                    <Alert tone="warning" style={{ marginTop: 12 }} data-testid="no-dp-warning">
                      Sede di consegna mancante: non è possibile calcolare il vettore consigliato
                      né assegnare il Piano Carichi finché non viene definita la destinazione.
                    </Alert>
                  );
                }
                // Assigned to a mutable LoadPlan → allow "Cambia Piano Carichi"
                const mutablePlan = prep.load_plan &&
                    ["DRAFT", "SENT_TO_CARRIER"].includes(prep.load_plan.status);
                if (mutablePlan && draftShip && prep.delivery_point) {
                  return (
                    <div style={{ marginTop: 12 }}>
                      {!changeLpOpen ? (
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <Button size="sm" variant="outline"
                                  data-testid="btn-change-load-plan"
                                  onClick={() => setChangeLpOpen(true)}>
                            Cambia Piano Carichi
                          </Button>
                        </div>
                      ) : (
                        <AssignToLoadPlanCard
                          orderId={prep.order.id}
                          shipmentId={draftShip.id}
                          deliveryPoint={prep.delivery_point}
                          estimatedPallets={prep.order?.estimated_pallets_total}
                          onAssigned={() => { setChangeLpOpen(false); setReloadTick(t => t + 1); }}
                          changeMode
                          currentPlan={{
                            id: prep.load_plan.id,
                            number: prep.load_plan.number,
                            carrier_id: prep.load_plan.carrier_id,
                            carrier_name: prep.load_plan.carrier_name || prep.carrier?.business_name,
                            loading_date: prep.load_plan.loading_date,
                            status: prep.load_plan.status,
                          }}
                        />
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </Card.Body></Card>
          </Section>
        </>
      )}

      {st === "PROCESSING" && prep.order?.delivery_mode !== "PICKUP" && consuntivaMode && (
        <ConsuntivaWizard
          prep={prep}
          selCarrier={selCarrier} setSelCarrier={setSelCarrier}
          expDate={expDate} setExpDate={setExpDate}
          window={window} setWindow={setWindow}
          pallets={pallets} setPallets={setPallets}
          palletsErr={palletsErr} setPalletsErr={setPalletsErr}
          notes={notes} setNotes={setNotes}
          linesToShip={linesToShip} setLinesToShip={setLinesToShip}
          ddtFile={ddtFile} setDdtFile={setDdtFile}
          ddtNumber={ddtNumber} setDdtNumber={setDdtNumber}
          ddtDate={ddtDate} setDdtDate={setDdtDate}
          carriers={carriers}
          onBack={() => setConsuntivaMode(false)}
          onSubmitted={setDone}
          setErr={setErr}
          token={token}
          orderId={orderId}
        />
      )}



      {(st === "SHIPPING" || st === "IN_DELIVERY" || st === "DELIVERED") && (
        <ExistingShipmentsSection
          shipments={prep.existing_shipments}
          orderId={prep.order.id}
          orderStatus={st}
          onEtaUpdated={() => setReloadTick((t) => t + 1)}
        />
      )}
    </>
  );
}
